import React from 'react';
import { Thumbnail } from '@remotion/player';
import { Easing, Img, interpolate, staticFile, useCurrentFrame, useVideoConfig } from 'remotion';
import { elderlyCare13Elements } from './elderlyCare13Elements';

const teal = {
  50: '#f0f9f7',
  100: '#dcf2ec',
  200: '#bce5da',
  300: '#8dd1c0',
  400: '#5ab8a3',
  500: '#3e9d87',
  700: '#296659',
  900: '#104c42'
};

const slate = {
  50: '#f8fafc',
  100: '#e2e8f0',
  200: '#cbd5e1',
  500: '#64748b',
  700: '#334155',
  900: '#172e4e'
};

const warm = {
  cream: '#fff8ed',
  sand: '#f7dfb8',
  gold: '#f5b85a',
  coral: '#f28d7a',
  rose: '#f6c4bb',
  skin: '#d99b75',
  skinLight: '#f1c29e'
};

const GRAPHIC_CONFIG = {
  hero: { width: 900, height: 680, durationInFrames: 360 },
  why: { width: 900, height: 640, durationInFrames: 330 },
  cta: { width: 900, height: 260, durationInFrames: 240 },
  rating: { width: 280, height: 58, durationInFrames: 180 },
  'service-personal': { width: 280, height: 280, durationInFrames: 210 },
  'service-companion': { width: 280, height: 280, durationInFrames: 240 },
  'service-family': { width: 280, height: 280, durationInFrames: 210 },
  'service-daily': { width: 280, height: 280, durationInFrames: 210 },
  'service-dementia': { width: 280, height: 280, durationInFrames: 230 },
  'service-livein': { width: 280, height: 280, durationInFrames: 240 },
  'service-respite': { width: 280, height: 280, durationInFrames: 210 },
  'service-rehab': { width: 280, height: 280, durationInFrames: 240 },
  'about-nursing-home': { width: 1000, height: 730, durationInFrames: 360 },
  'about-elderly-care': { width: 1069, height: 848, durationInFrames: 390 },
  'services-hero': { width: 900, height: 520, durationInFrames: 300 },
  'services-personal': { width: 640, height: 520, durationInFrames: 300 },
  'services-companion': { width: 640, height: 520, durationInFrames: 300 },
  'services-dementia': { width: 640, height: 520, durationInFrames: 300 },
  'services-livein': { width: 640, height: 520, durationInFrames: 300 },
  'services-respite': { width: 640, height: 520, durationInFrames: 300 },
  'services-rehab': { width: 640, height: 520, durationInFrames: 300 },
  'services-quality': { width: 640, height: 520, durationInFrames: 300 },
  'services-day': { width: 280, height: 280, durationInFrames: 240 },
  'services-night': { width: 280, height: 280, durationInFrames: 240 },
  'services-palliative': { width: 280, height: 280, durationInFrames: 240 },
  'services-family-support': { width: 280, height: 280, durationInFrames: 240 },
  'service-map': { width: 1100, height: 620, durationInFrames: 420 },
  'area-map-north-tyneside': { width: 640, height: 320, durationInFrames: 300 },
  'area-map-newcastle': { width: 640, height: 320, durationInFrames: 300 },
  'area-map-gateshead': { width: 640, height: 320, durationInFrames: 300 },
  'area-map-hexham': { width: 640, height: 320, durationInFrames: 300 },
  'area-map-corbridge': { width: 640, height: 320, durationInFrames: 300 },
  'area-map-riding-mill': { width: 640, height: 320, durationInFrames: 300 },
  'area-map-stocksfield': { width: 640, height: 320, durationInFrames: 300 },
  'area-map-prudhoe': { width: 640, height: 320, durationInFrames: 300 }
};

const easeOut = Easing.bezier(0.16, 1, 0.3, 1);
const easeInOut = Easing.bezier(0.45, 0, 0.55, 1);
const LONG_RUNNING_DURATION_IN_FRAMES = 36000;
const tickerStores = new Map();

const useRemotionTicker = (fps, durationInFrames) => {
  const store = React.useMemo(() => {
    const key = `${fps}:${durationInFrames}`;

    if (tickerStores.has(key)) {
      return tickerStores.get(key);
    }

    const initialFrame = Math.round(fps * 1.1);
    const subscribers = new Set();
    let frame = initialFrame;
    let animationFrame = 0;

    const notify = () => {
      subscribers.forEach((subscriber) => subscriber());
    };

    const stop = () => {
      if (!animationFrame) {
        return;
      }

      cancelAnimationFrame(animationFrame);
      animationFrame = 0;
    };

    const start = () => {
      if (animationFrame || typeof window === 'undefined') {
        return;
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduceMotion) {
        return;
      }

      const startedAt = performance.now() - (frame / fps) * 1000;

      const tick = (now) => {
        const elapsedSeconds = (now - startedAt) / 1000;
        frame = Math.floor(elapsedSeconds * fps) % durationInFrames;
        notify();
        animationFrame = requestAnimationFrame(tick);
      };

      animationFrame = requestAnimationFrame(tick);
    };

    const tickerStore = {
      getSnapshot: () => frame,
      subscribe: (subscriber) => {
        subscribers.add(subscriber);
        start();

        return () => {
          subscribers.delete(subscriber);

          if (subscribers.size === 0) {
            stop();
          }
        };
      }
    };

    tickerStores.set(key, tickerStore);
    return tickerStore;
  }, [durationInFrames, fps]);

  return React.useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
};

const fadeIn = (frame, fps, delaySeconds = 0, durationSeconds = 0.9) =>
  interpolate(
    frame,
    [delaySeconds * fps, (delaySeconds + durationSeconds) * fps],
    [0, 1],
    {
      easing: easeOut,
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp'
    }
  );

const slide = (frame, fps, delaySeconds = 0, distance = 24) => {
  const progress = fadeIn(frame, fps, delaySeconds, 0.9);
  return interpolate(progress, [0, 1], [distance, 0]);
};

const loopProgress = (frame, fps, seconds, offsetSeconds = 0) => {
  const total = seconds * fps;
  return ((frame + offsetSeconds * fps) % total) / total;
};

const wave = (frame, fps, seconds, amplitude = 1, phase = 0) =>
  Math.sin(((frame / fps) / seconds) * Math.PI * 2 + phase) * amplitude;

const pulse01 = (frame, fps, seconds, phase = 0) =>
  (Math.sin(((frame / fps) / seconds) * Math.PI * 2 + phase) + 1) / 2;

const drift = (frame, fps, seconds, amplitude = 1, phase = 0) =>
  Math.cos(((frame / fps) / seconds) * Math.PI * 2 + phase) * amplitude;

const starPath =
  'M28 4.5l6.4 13 14.3 2.1-10.4 10.1 2.5 14.2L28 37.2 15.2 44l2.5-14.2L7.3 19.6l14.3-2.1L28 4.5z';

const SERVICE_MAP_BOUNDS = {
  minLon: -2.27,
  maxLon: -1.38,
  minLat: 54.944,
  maxLat: 55.026
};

const SERVICE_MAP_PADDING = 24;

const SERVICE_ROUTE = [
  [-2.246, 54.974],
  [-2.101, 54.971],
  [-2.018, 54.974],
  [-1.974, 54.948],
  [-1.905, 54.948],
  [-1.851, 54.961],
  [-1.823, 54.976],
  [-1.741, 54.987],
  [-1.618, 54.978],
  [-1.603, 54.953],
  [-1.534, 54.991],
  [-1.444, 55.01],
  [-1.423, 55.018]
];

const SERVICE_AREA_MAPS = {
  'north-tyneside': {
    name: 'North Tyneside',
    center: [-1.488, 55.019],
    color: teal[700],
    accent: warm.gold,
    coastline: true,
    points: [
      { name: 'North Shields', short: 'North Shields', coord: [-1.444, 55.01], type: 'major' },
      { name: 'Tynemouth', short: 'Tynemouth', coord: [-1.423, 55.018], type: 'major' },
      { name: 'Whitley Bay', short: 'Whitley Bay', coord: [-1.445, 55.044], type: 'major' },
      { name: 'Wallsend', short: 'Wallsend', coord: [-1.534, 54.991], type: 'major' },
      { name: 'Cullercoats', short: 'Cullercoats', coord: [-1.433, 55.033], type: 'nearby' },
      { name: 'Longbenton', short: 'Longbenton', coord: [-1.592, 55.006], type: 'nearby' },
      { name: 'Monkseaton', short: 'Monkseaton', coord: [-1.461, 55.042], type: 'nearby' },
      { name: 'Shiremoor', short: 'Shiremoor', coord: [-1.507, 55.037], type: 'nearby' }
    ]
  },
  newcastle: {
    name: 'Newcastle',
    center: [-1.618, 54.978],
    color: '#2563eb',
    accent: warm.coral,
    points: [
      { name: 'Newcastle City Centre', short: 'City Centre', coord: [-1.618, 54.978], type: 'major' },
      { name: 'Jesmond', short: 'Jesmond', coord: [-1.605, 54.992], type: 'major' },
      { name: 'Heaton', short: 'Heaton', coord: [-1.581, 54.988], type: 'major' },
      { name: 'Gosforth', short: 'Gosforth', coord: [-1.621, 55.007], type: 'major' },
      { name: 'Byker', short: 'Byker', coord: [-1.579, 54.976], type: 'nearby' },
      { name: 'Fawdon', short: 'Fawdon', coord: [-1.642, 55.014], type: 'nearby' },
      { name: 'West Denton', short: 'West Denton', coord: [-1.707, 54.996], type: 'nearby' },
      { name: 'South Jesmond', short: 'S. Jesmond', coord: [-1.606, 54.987], type: 'nearby' }
    ]
  },
  gateshead: {
    name: 'Gateshead',
    center: [-1.603, 54.953],
    color: '#7c3aed',
    accent: warm.gold,
    points: [
      { name: 'Gateshead Town Centre', short: 'Town Centre', coord: [-1.603, 54.953], type: 'major' },
      { name: 'Bensham', short: 'Bensham', coord: [-1.626, 54.954], type: 'major' },
      { name: 'Whickham', short: 'Whickham', coord: [-1.676, 54.945], type: 'major' },
      { name: 'Felling', short: 'Felling', coord: [-1.571, 54.954], type: 'major' },
      { name: 'Rowlands Gill', short: 'Rowlands Gill', coord: [-1.744, 54.919], type: 'nearby' },
      { name: 'Low Fell', short: 'Low Fell', coord: [-1.602, 54.934], type: 'nearby' },
      { name: 'Birtley', short: 'Birtley', coord: [-1.576, 54.899], type: 'nearby' },
      { name: 'Dunston', short: 'Dunston', coord: [-1.642, 54.953], type: 'nearby' }
    ]
  },
  hexham: {
    name: 'Hexham',
    center: [-2.101, 54.971],
    color: '#b45309',
    accent: teal[500],
    points: [
      { name: 'Hexham', short: 'Hexham', coord: [-2.101, 54.971], type: 'major' },
      { name: 'Bellingham', short: 'Bellingham', coord: [-2.253, 55.145], type: 'major' },
      { name: 'Haydon Bridge', short: 'Haydon Bridge', coord: [-2.246, 54.974], type: 'major' },
      { name: 'Prudhoe Corridor', short: 'A69 corridor', coord: [-1.93, 54.96], type: 'major' },
      { name: 'Allendale', short: 'Allendale', coord: [-2.254, 54.897], type: 'nearby' },
      { name: 'Wark', short: 'Wark', coord: [-2.213, 55.09], type: 'nearby' },
      { name: 'Acomb', short: 'Acomb', coord: [-2.113, 54.992], type: 'nearby' },
      { name: 'Chollerford', short: 'Chollerford', coord: [-2.127, 55.025], type: 'nearby' }
    ]
  },
  corbridge: {
    name: 'Corbridge',
    center: [-2.018, 54.974],
    color: '#0f766e',
    accent: warm.coral,
    points: [
      { name: 'Corbridge', short: 'Corbridge', coord: [-2.018, 54.974], type: 'major' },
      { name: 'Bywell', short: 'Bywell', coord: [-1.928, 54.949], type: 'major' },
      { name: 'Newbiggin', short: 'Newbiggin', coord: [-2.044, 54.997], type: 'major' },
      { name: 'Fourstones', short: 'Fourstones', coord: [-2.151, 54.981], type: 'major' },
      { name: 'Henshaw', short: 'Henshaw', coord: [-2.325, 54.985], type: 'nearby' },
      { name: 'Chollerford', short: 'Chollerford', coord: [-2.127, 55.025], type: 'nearby' },
      { name: 'Sandhoe', short: 'Sandhoe', coord: [-2.064, 54.986], type: 'nearby' },
      { name: 'Aydon', short: 'Aydon', coord: [-2.003, 54.989], type: 'nearby' }
    ]
  },
  'riding-mill': {
    name: 'Riding Mill',
    center: [-1.974, 54.948],
    color: '#be123c',
    accent: teal[500],
    points: [
      { name: 'Riding Mill', short: 'Riding Mill', coord: [-1.974, 54.948], type: 'major' },
      { name: 'Newbrough', short: 'Newbrough', coord: [-2.197, 55.007], type: 'major' },
      { name: 'Bywell', short: 'Bywell', coord: [-1.928, 54.949], type: 'major' },
      { name: 'Plenmeller', short: 'Plenmeller', coord: [-2.381, 54.974], type: 'major' },
      { name: 'Broomhaugh', short: 'Broomhaugh', coord: [-1.972, 54.95], type: 'nearby' },
      { name: 'Slaley', short: 'Slaley', coord: [-2.038, 54.912], type: 'nearby' },
      { name: 'Healey', short: 'Healey', coord: [-1.989, 54.926], type: 'nearby' },
      { name: 'Stocksfield', short: 'Stocksfield', coord: [-1.905, 54.948], type: 'nearby' }
    ]
  },
  stocksfield: {
    name: 'Stocksfield',
    center: [-1.905, 54.948],
    color: '#047857',
    accent: warm.gold,
    points: [
      { name: 'Stocksfield', short: 'Stocksfield', coord: [-1.905, 54.948], type: 'major' },
      { name: 'Broomley', short: 'Broomley', coord: [-1.919, 54.96], type: 'major' },
      { name: 'New Ridley', short: 'New Ridley', coord: [-1.888, 54.946], type: 'major' },
      { name: 'Bywell', short: 'Bywell', coord: [-1.928, 54.949], type: 'major' },
      { name: 'Mickley', short: 'Mickley', coord: [-1.876, 54.951], type: 'nearby' },
      { name: 'Ovingham', short: 'Ovingham', coord: [-1.87, 54.968], type: 'nearby' },
      { name: 'Broomhaugh', short: 'Broomhaugh', coord: [-1.972, 54.95], type: 'nearby' },
      { name: 'Hindley', short: 'Hindley', coord: [-1.934, 54.931], type: 'nearby' }
    ]
  },
  prudhoe: {
    name: 'Prudhoe',
    center: [-1.851, 54.961],
    color: '#c2410c',
    accent: '#2563eb',
    points: [
      { name: 'Prudhoe', short: 'Prudhoe', coord: [-1.851, 54.961], type: 'major' },
      { name: 'Wylam', short: 'Wylam', coord: [-1.823, 54.976], type: 'major' },
      { name: 'Ovingham', short: 'Ovingham', coord: [-1.87, 54.968], type: 'major' },
      { name: 'Newburn', short: 'Newburn', coord: [-1.741, 54.987], type: 'major' },
      { name: 'Crawcrook', short: 'Crawcrook', coord: [-1.793, 54.967], type: 'nearby' },
      { name: 'Mickley', short: 'Mickley', coord: [-1.876, 54.951], type: 'nearby' },
      { name: 'Ryton', short: 'Ryton', coord: [-1.763, 54.974], type: 'nearby' },
      { name: 'Heddon-on-the-Wall', short: 'Heddon', coord: [-1.79, 54.996], type: 'nearby' }
    ]
  }
};

const serviceAreaEntries = Object.entries(SERVICE_AREA_MAPS);

const SERVICE_MAP_LABEL_OFFSETS = {
  hexham: { dx: -128, dy: -64, width: 94 },
  corbridge: { dx: -76, dy: -104, width: 112 },
  'riding-mill': { dx: -104, dy: -82, width: 116 },
  stocksfield: { dx: 20, dy: -86, width: 110 },
  prudhoe: { dx: 4, dy: -78, width: 98 },
  newcastle: { dx: -6, dy: -74, width: 108 },
  gateshead: { dx: -126, dy: -84, width: 106 },
  'north-tyneside': { dx: -238, dy: 38, width: 136 }
};

const AREA_MAP_PADDING = 12;

const AREA_MAP_LABEL_OFFSETS = {
  'north-tyneside': {
    'North Shields': { dx: 34, dy: 8, width: 112 },
    Tynemouth: { dx: -126, dy: 30, width: 98 },
    'Whitley Bay': { dx: 22, dy: -46, width: 104 },
    Wallsend: { dx: 22, dy: -18, width: 86 }
  },
  newcastle: {
    'Newcastle City Centre': { dx: 20, dy: -48, width: 108, label: 'City Centre' },
    Jesmond: { dx: 24, dy: -34, width: 82 },
    Heaton: { dx: 22, dy: 18, width: 74 },
    Gosforth: { dx: -112, dy: -50, width: 92 }
  },
  gateshead: {
    'Gateshead Town Centre': { dx: -116, dy: -50, width: 104, label: 'Town Centre' },
    Bensham: { dx: -114, dy: 18, width: 86 },
    Whickham: { dx: -112, dy: -18, width: 92 },
    Felling: { dx: 20, dy: -12, width: 74 }
  },
  hexham: {
    Hexham: { dx: 22, dy: -44, width: 78 },
    Bellingham: { dx: 20, dy: -34, width: 100 },
    'Haydon Bridge': { dx: -136, dy: 18, width: 124 },
    'Prudhoe Corridor': { dx: -52, dy: 34, width: 112, label: 'A69 corridor' }
  },
  corbridge: {
    Corbridge: { dx: 20, dy: -42, width: 92 },
    Bywell: { dx: -104, dy: 34, width: 76 },
    Newbiggin: { dx: 20, dy: -42, width: 94 },
    Fourstones: { dx: -116, dy: -18, width: 96 }
  },
  'riding-mill': {
    'Riding Mill': { dx: 34, dy: -42, width: 104 },
    Newbrough: { dx: 22, dy: -40, width: 102 },
    Bywell: { dx: -104, dy: 36, width: 76 },
    Plenmeller: { dx: 20, dy: -32, width: 100 }
  },
  stocksfield: {
    Stocksfield: { dx: 22, dy: -42, width: 98 },
    Broomley: { dx: -110, dy: -40, width: 88 },
    'New Ridley': { dx: 22, dy: 20, width: 98 },
    Bywell: { dx: -98, dy: 30, width: 76 }
  },
  prudhoe: {
    Prudhoe: { dx: -104, dy: -46, width: 86 },
    Wylam: { dx: 22, dy: -44, width: 72 },
    Ovingham: { dx: -114, dy: 22, width: 94 },
    Newburn: { dx: 22, dy: 20, width: 88 }
  }
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const projectCoordinate = ([lon, lat], bounds, width, height, padding = 70) => {
  const usableWidth = width - padding * 2;
  const usableHeight = height - padding * 2;
  const x = padding + ((lon - bounds.minLon) / (bounds.maxLon - bounds.minLon)) * usableWidth;
  const y = padding + ((bounds.maxLat - lat) / (bounds.maxLat - bounds.minLat)) * usableHeight;

  return { x, y };
};

const buildPath = (coordinates, bounds, width, height, padding = 70) =>
  coordinates
    .map((coordinate, index) => {
      const point = projectCoordinate(coordinate, bounds, width, height, padding);
      return `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`;
    })
    .join(' ');

const getAreaBounds = (area) => {
  const majorCoordinates = area.points
    .filter((point) => point.type === 'major')
    .map((point) => point.coord);
  const coordinates = [area.center, ...(majorCoordinates.length > 0 ? majorCoordinates : area.points.map((point) => point.coord))];
  const lons = coordinates.map(([lon]) => lon);
  const lats = coordinates.map(([, lat]) => lat);
  const lonPadding = Math.max((Math.max(...lons) - Math.min(...lons)) * 0.08, 0.006);
  const latPadding = Math.max((Math.max(...lats) - Math.min(...lats)) * 0.12, 0.006);

  return {
    minLon: Math.min(...lons) - lonPadding,
    maxLon: Math.max(...lons) + lonPadding,
    minLat: Math.min(...lats) - latPadding,
    maxLat: Math.max(...lats) + latPadding
  };
};

export const RemotionGraphic = ({
  variant,
  className = '',
  ariaLabel,
  fps = 30
}) => {
  const config = GRAPHIC_CONFIG[variant] || GRAPHIC_CONFIG.hero;
  const frame = useRemotionTicker(fps, LONG_RUNNING_DURATION_IN_FRAMES);

  return (
    <div className={className} aria-label={ariaLabel} role={ariaLabel ? 'img' : undefined}>
      <Thumbnail
        component={OdaraGraphicScene}
        inputProps={{ variant }}
        frameToDisplay={frame}
        durationInFrames={LONG_RUNNING_DURATION_IN_FRAMES}
        compositionWidth={config.width}
        compositionHeight={config.height}
        fps={fps}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

const OdaraGraphicScene = ({ variant }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  if (variant === 'hero') {
    return <HeroCareScene frame={frame} fps={fps} />;
  }

  if (variant === 'why') {
    return <WhyFamiliesScene frame={frame} fps={fps} />;
  }

  if (variant === 'cta') {
    return <CareConversationScene frame={frame} fps={fps} />;
  }

  if (variant === 'rating') {
    return <RatingScene frame={frame} fps={fps} />;
  }

  if (variant === 'service-map') {
    return <ServiceCoverageMapScene frame={frame} fps={fps} />;
  }

  if (variant.startsWith('area-map-')) {
    return <AreaCoverageMapScene frame={frame} fps={fps} slug={variant.replace('area-map-', '')} />;
  }

  if (variant === 'about-nursing-home') {
    return <AboutNursingHomeScene frame={frame} fps={fps} />;
  }

  if (variant === 'about-elderly-care') {
    return <ElderlyCareStoryScene frame={frame} fps={fps} />;
  }

  if (variant === 'services-hero') {
    return <ServicesHeroScene frame={frame} fps={fps} />;
  }

  if (variant.startsWith('services-')) {
    return <ServicesTopicScene frame={frame} fps={fps} type={variant.replace('services-', '')} />;
  }

  if (variant === 'service-dementia') {
    return <DementiaCareIcon frame={frame} fps={fps} />;
  }

  if (variant === 'service-livein') {
    return <LiveInCareIcon frame={frame} fps={fps} />;
  }

  if (variant === 'service-respite') {
    return <RespiteCareIcon frame={frame} fps={fps} />;
  }

  if (variant === 'service-rehab') {
    return <RehabSupportIcon frame={frame} fps={fps} />;
  }

  return <ServiceIconScene frame={frame} fps={fps} type={variant.replace('service-', '')} />;
};

const MapPin = ({ x, y, color, accent, scale = 1, label, labelSide = 'right', active = false }) => {
  const labelX = labelSide === 'left' ? -12 : 12;
  const textAnchor = labelSide === 'left' ? 'end' : 'start';

  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <circle r={active ? 20 : 15} fill={color} opacity={active ? 0.16 : 0.1} />
      <circle r="8" fill="#ffffff" stroke={color} strokeWidth="4" />
      <circle r="3.2" fill={accent || color} />
      {label ? (
        <g transform={`translate(${labelX} -4)`}>
          <rect
            x={labelSide === 'left' ? -112 : 0}
            y="-14"
            width="112"
            height="27"
            rx="13.5"
            fill="#ffffff"
            opacity="0.94"
          />
          <text
            x={labelSide === 'left' ? -12 : 12}
            y="4"
            fill={slate[900]}
            fontSize="14"
            fontWeight="700"
            textAnchor={textAnchor}
            fontFamily="Inter, Arial, sans-serif"
          >
            {label}
          </text>
        </g>
      ) : null}
    </g>
  );
};

const MapCalloutLabel = ({ x, y, color, label, offset, active }) => {
  const labelX = x + offset.dx;
  const labelY = y + offset.dy;
  const width = offset.width || 112;
  const height = 28;
  const connectorX = offset.dx < 0 ? labelX + width : labelX;
  const connectorY = labelY + height / 2;

  return (
    <g>
      <path
        d={`M${x.toFixed(1)} ${y.toFixed(1)}L${connectorX.toFixed(1)} ${connectorY.toFixed(1)}`}
        fill="none"
        stroke={color}
        strokeWidth={active ? 3 : 2}
        strokeLinecap="round"
        opacity={active ? 0.58 : 0.32}
      />
      <rect
        x={labelX}
        y={labelY}
        width={width}
        height={height}
        rx="14"
        fill="#ffffff"
        stroke={active ? color : '#ffffff'}
        strokeWidth={active ? 2 : 1}
        opacity="0.96"
      />
      <text
        x={labelX + width / 2}
        y={labelY + 18}
        fill={slate[900]}
        fontSize="13"
        fontWeight="800"
        textAnchor="middle"
        fontFamily="Inter, Arial, sans-serif"
      >
        {label}
      </text>
    </g>
  );
};

const ServiceCoverageMapScene = ({ frame, fps }) => {
  const { width, height } = useVideoConfig();
  const enter = fadeIn(frame, fps, 0, 1);
  const routeProgress = loopProgress(frame, fps, 8);
  const activeIndex = Math.floor(loopProgress(frame, fps, 18) * serviceAreaEntries.length) % serviceAreaEntries.length;
  const riverPath = buildPath(SERVICE_ROUTE, SERVICE_MAP_BOUNDS, width, height, SERVICE_MAP_PADDING);
  const routeLength = 1250;
  const gridShift = interpolate(loopProgress(frame, fps, 18), [0, 1], [0, 72], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <defs>
        <linearGradient id="service-map-land" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#fff8ed" />
          <stop offset="0.48" stopColor="#f0f9f7" />
          <stop offset="1" stopColor="#eef2ff" />
        </linearGradient>
        <linearGradient id="service-map-water" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#dff4ef" />
          <stop offset="1" stopColor="#bfdbfe" />
        </linearGradient>
        <filter id="service-map-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="18" stdDeviation="18" floodColor="#172e4e" floodOpacity="0.16" />
        </filter>
      </defs>

      <rect width={width} height={height} rx="34" fill="url(#service-map-land)" />
      <path
        d={`M${width * 0.82} 0C${width * 0.76} 95 ${width * 0.9} 147 ${width * 0.84} 245C${width * 0.78} 342 ${width * 0.92} 430 ${width * 0.86} ${height}H${width}V0z`}
        fill="url(#service-map-water)"
        opacity="0.9"
      />
      <path
        d={`M${width * 0.83} 26C${width * 0.78} 118 ${width * 0.9} 173 ${width * 0.84} 250C${width * 0.8} 313 ${width * 0.88} 388 ${width * 0.85} 518`}
        fill="none"
        stroke="#ffffff"
        strokeWidth="9"
        strokeLinecap="round"
        opacity="0.72"
      />
      {[...Array(9)].map((_, index) => (
        <path
          key={`map-grid-h-${index}`}
          d={`M0 ${70 + index * 62 + gridShift % 62}H${width}`}
          stroke="#ffffff"
          strokeWidth="2"
          opacity="0.26"
        />
      ))}
      {[...Array(12)].map((_, index) => (
        <path
          key={`map-grid-v-${index}`}
          d={`M${44 + index * 82 + gridShift % 82} 0V${height}`}
          stroke="#ffffff"
          strokeWidth="2"
          opacity="0.2"
        />
      ))}

      <g opacity={enter} filter="url(#service-map-shadow)">
        <path d={riverPath} fill="none" stroke="#93c5fd" strokeWidth="26" strokeLinecap="round" strokeLinejoin="round" opacity="0.42" />
        <path d={riverPath} fill="none" stroke="#ffffff" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" opacity="0.82" />
        <path
          d={riverPath}
          fill="none"
          stroke={teal[700]}
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={`${routeLength * 0.18} ${routeLength}`}
          strokeDashoffset={routeLength - routeProgress * routeLength}
          opacity="0.86"
        />
      </g>

      <path
        d={buildPath(
          [
            [-2.38, 55.11],
            [-2.16, 55.06],
            [-1.98, 55.02],
            [-1.82, 54.99],
            [-1.62, 54.98],
            [-1.44, 55.02]
          ],
          SERVICE_MAP_BOUNDS,
          width,
          height,
          SERVICE_MAP_PADDING
        )}
        fill="none"
        stroke={warm.gold}
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray="16 18"
        opacity="0.62"
      />

      {serviceAreaEntries.map(([slug, area], index) => {
        const point = projectCoordinate(area.center, SERVICE_MAP_BOUNDS, width, height, SERVICE_MAP_PADDING);
        const active = index === activeIndex;
        const pulse = pulse01(frame, fps, 2.4, index * 0.7);
        const labelSide = point.x > width * 0.7 ? 'left' : 'right';

        return (
          <g key={slug} opacity={0.88 + (active ? 0.12 : 0)}>
            <circle
              cx={point.x}
              cy={point.y}
              r={active ? 46 + pulse * 16 : 27 + pulse * 5}
              fill={area.color}
              opacity={active ? 0.18 : 0.08}
            />
            <MapPin
              x={point.x}
              y={point.y}
              color={area.color}
              accent={area.accent}
              scale={active ? 1.08 : 0.94}
              active={active}
            />
            <MapCalloutLabel
              x={point.x}
              y={point.y}
              color={area.color}
              label={area.name}
              offset={SERVICE_MAP_LABEL_OFFSETS[slug] || { dx: labelSide === 'left' ? -124 : 18, dy: -32, width: 112 }}
              active={active}
            />
          </g>
        );
      })}

      <g transform="translate(42 42)" opacity={enter}>
        <rect width="245" height="98" rx="22" fill="#ffffff" opacity="0.94" />
        <text x="24" y="35" fill={slate[900]} fontSize="18" fontWeight="800" fontFamily="Inter, Arial, sans-serif">
          North East coverage
        </text>
        <text x="24" y="64" fill={slate[500]} fontSize="14" fontWeight="600" fontFamily="Inter, Arial, sans-serif">
          Tyne Valley, Newcastle, coast
        </text>
        <path d="M24 80h72" stroke={teal[700]} strokeWidth="5" strokeLinecap="round" />
        <path d="M114 80h72" stroke={warm.gold} strokeWidth="5" strokeLinecap="round" strokeDasharray="12 10" />
      </g>
    </svg>
  );
};

const AreaCoverageMapScene = ({ frame, fps, slug }) => {
  const area = SERVICE_AREA_MAPS[slug] || SERVICE_AREA_MAPS.newcastle;
  const { width, height } = useVideoConfig();
  const bounds = getAreaBounds(area);
  const centerPoint = projectCoordinate(area.center, bounds, width, height, AREA_MAP_PADDING);
  const enter = fadeIn(frame, fps, 0, 0.8);
  const draw = fadeIn(frame, fps, 0.22, 1);
  const routeLength = 780;
  const visibleRoute = area.points
    .filter((point) => point.type === 'major')
    .map((point) => point.coord);
  const routePath = buildPath(visibleRoute, bounds, width, height, AREA_MAP_PADDING);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <defs>
        <linearGradient id={`area-map-bg-${slug}`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.5" stopColor="#f0f9f7" />
          <stop offset="1" stopColor="#fff7ed" />
        </linearGradient>
        <filter id={`area-map-shadow-${slug}`} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="12" stdDeviation="12" floodColor="#172e4e" floodOpacity="0.12" />
        </filter>
      </defs>

      <rect width={width} height={height} fill={`url(#area-map-bg-${slug})`} />
      {area.coastline ? (
        <path
          d={`M${width * 0.77} 0C${width * 0.68} 78 ${width * 0.88} 132 ${width * 0.78} 204C${width * 0.72} 250 ${width * 0.78} 292 ${width * 0.74} ${height}H${width}V0z`}
          fill="#bfdbfe"
          opacity="0.58"
        />
      ) : null}

      {[...Array(5)].map((_, index) => (
        <path
          key={`local-grid-h-${index}`}
          d={`M0 ${58 + index * 54}H${width}`}
          stroke="#ffffff"
          strokeWidth="2"
          opacity="0.48"
        />
      ))}
      {[...Array(8)].map((_, index) => (
        <path
          key={`local-grid-v-${index}`}
          d={`M${62 + index * 72} 0V${height}`}
          stroke="#ffffff"
          strokeWidth="2"
          opacity="0.4"
        />
      ))}

      <g opacity={enter} filter={`url(#area-map-shadow-${slug})`}>
        {visibleRoute.map((coordinate, index) => {
          const point = projectCoordinate(coordinate, bounds, width, height, AREA_MAP_PADDING);
          return (
            <circle
              key={`coverage-zone-${index}`}
              cx={point.x}
              cy={point.y}
              r={index % 2 === 0 ? 58 : 48}
              fill={area.color}
              opacity="0.075"
            />
          );
        })}
        <path
          d={routePath}
          fill="none"
          stroke="#ffffff"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.9"
        />
        <path
          d={routePath}
          fill="none"
          stroke={area.accent}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={`${routeLength * 0.45} ${routeLength}`}
          strokeDashoffset={routeLength - draw * routeLength}
          opacity="0.74"
        />
      </g>

      {area.points.map((point, index) => {
        const { x, y } = projectCoordinate(point.coord, bounds, width, height, AREA_MAP_PADDING);
        const isMajor = point.type === 'major';
        const pointEnter = fadeIn(frame, fps, 0.15 + index * 0.035, 0.5);
        const pulse = pulse01(frame, fps, 2.5, index * 0.5);
        const labelSide = x > width * 0.7 ? 'left' : 'right';
        const configuredLabel = AREA_MAP_LABEL_OFFSETS[slug]?.[point.name];
        const labelText = configuredLabel?.label || point.short;
        const labelWidth = configuredLabel?.width || Math.max(72, Math.min(126, labelText.length * 7 + 28));
        const defaultDx = labelSide === 'left' ? -labelWidth - 12 : 12;
        const labelX = clamp(x + (configuredLabel?.dx ?? defaultDx), 8, width - labelWidth - 8);
        const labelY = clamp(y + (configuredLabel?.dy ?? -16), 10, height - 34);

        return (
          <g
            key={point.name}
            opacity={pointEnter}
            transform={`translate(${0} ${slide(frame, fps, 0.15 + index * 0.035, 10)})`}
          >
            <circle
              cx={x}
              cy={y}
              r={isMajor ? 13 + pulse * 2 : 7 + pulse * 1.5}
              fill={isMajor ? area.color : area.accent}
              opacity={isMajor ? 0.22 : 0.16}
            />
            <circle
              cx={x}
              cy={y}
              r={isMajor ? 5.8 : 4.2}
              fill="#ffffff"
              stroke={isMajor ? area.color : area.accent}
              strokeWidth={isMajor ? 3 : 2.5}
            />
            {isMajor ? (
              <g transform={`translate(${labelX} ${labelY})`}>
                <rect
                  x="0"
                  y="0"
                  width={labelWidth}
                  height="23"
                  rx="11.5"
                  fill="#ffffff"
                  opacity="0.92"
                />
                <text
                  x={labelWidth / 2}
                  y="16"
                  fill={slate[900]}
                  fontSize="12"
                  fontWeight="700"
                  textAnchor="middle"
                  fontFamily="Inter, Arial, sans-serif"
                >
                  {labelText}
                </text>
              </g>
            ) : null}
          </g>
        );
      })}

      <g opacity={enter}>
        <circle cx={centerPoint.x} cy={centerPoint.y} r={28 + pulse01(frame, fps, 2.2) * 10} fill={area.color} opacity="0.16" />
        <MapPin x={centerPoint.x} y={centerPoint.y} color={area.color} accent={area.accent} scale={0.9} active />
      </g>

    </svg>
  );
};

const range = (start, end) => Array.from({ length: end - start + 1 }, (_, index) => start + index);

const ELDERLY_CARE_ELDER_HEAD = [4, 5, 17, 37, 38, 62, ...range(73, 83)];
const ELDERLY_CARE_ELDER_BODY = [6, 18, 19, 20, 21, 22, 23, 39, 40, 41, 42, 43, 44, 98, 99, 100, 101, 102, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115, 116, 117, 120, 121, 122, 164, 167, 168];
const ELDERLY_CARE_REACHING_ARM = [7, 34, 35, 36, ...range(84, 91), 103, 105, 166];
const ELDERLY_CARE_CAREGIVER_HEAD = [3, 13, ...range(54, 72)];
const ELDERLY_CARE_CAREGIVER_BODY = [10, 11, 12, ...range(24, 33), 118, 119, ...range(137, 143)];
const ELDERLY_CARE_CAREGIVER_HAND = [14, 16, ...range(92, 95)];
const ELDERLY_CARE_REAR_WHEEL = [128, 129, 130, 131, ...range(146, 159)];
const ELDERLY_CARE_FRONT_WHEEL = [132, 133, 134, 135, 163];
const ELDERLY_CARE_ANIMATED_INDEXES = new Set([
  ...ELDERLY_CARE_ELDER_HEAD,
  ...ELDERLY_CARE_ELDER_BODY,
  ...ELDERLY_CARE_REACHING_ARM,
  ...ELDERLY_CARE_CAREGIVER_HEAD,
  ...ELDERLY_CARE_CAREGIVER_BODY,
  ...ELDERLY_CARE_CAREGIVER_HAND,
  ...ELDERLY_CARE_REAR_WHEEL,
  ...ELDERLY_CARE_FRONT_WHEEL
]);
const ELDERLY_CARE_STATIC_INDEXES = elderlyCare13Elements
  .map((_, index) => index)
  .filter((index) => !ELDERLY_CARE_ANIMATED_INDEXES.has(index));

const renderElderlyCareElement = (index, extraProps = {}) => {
  const element = elderlyCare13Elements[index];
  const props = { ...element.attrs, ...extraProps };

  if (element.type === 'ellipse') {
    return <ellipse key={index} {...props} />;
  }

  if (element.type === 'polygon') {
    return <polygon key={index} {...props} />;
  }

  return <path key={index} {...props} />;
};

const renderElderlyCareLayer = (indexes, extraProps) =>
  indexes.map((index) => renderElderlyCareElement(index, extraProps));

const ElderlyCareStoryScene = ({ frame, fps }) => {
  const enter = fadeIn(frame, fps, 0, 1);
  const artworkLift = slide(frame, fps, 0, 22);
  const elderBob = wave(frame, fps, 5.6, 2.4, 0.4);
  const elderNod = wave(frame, fps, 6.8, 0.8, 1.1);
  const caregiverBob = wave(frame, fps, 5.1, 2.1, 1.7);
  const caregiverNod = wave(frame, fps, 6.2, 0.7, 2.3);
  const reach = wave(frame, fps, 4.3, 2.8, 0.7);
  const handCare = pulse01(frame, fps, 2.4);
  const wheelRock = wave(frame, fps, 5.8, 3.4, 0.2);
  const wheelLift = wave(frame, fps, 6.4, 1.2, 1.4);
  const backgroundBreath = pulse01(frame, fps, 6.8);
  const connectionDraw = fadeIn(frame, fps, 0.35, 1.25);
  const lineDrift = interpolate(loopProgress(frame, fps, 6.4), [0, 1], [0, -140], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });

  return (
    <svg viewBox="0 0 1069 848" width="1069" height="848">
      <defs>
        <linearGradient id="elderly-care-bg" x1="94" x2="1001" y1="14" y2="815" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fff8ed" />
          <stop offset="0.48" stopColor="#f0f9f7" />
          <stop offset="1" stopColor="#fff1f2" />
        </linearGradient>
        <linearGradient id="elderly-care-floor" x1="120" x2="936" y1="603" y2="829" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#dcf2ec" stopOpacity="0.75" />
          <stop offset="1" stopColor="#f7dfb8" stopOpacity="0.52" />
        </linearGradient>
        <filter id="elderly-care-shadow" x="-18%" y="-18%" width="136%" height="136%">
          <feDropShadow dx="0" dy="22" stdDeviation="18" floodColor="#172e4e" floodOpacity="0.14" />
        </filter>
      </defs>

      <rect width="1069" height="848" fill="url(#elderly-care-bg)" />
      <path
        d="M74 194C202 90 355 121 492 94c178-35 300-80 444 38 107 88 101 236 59 331-55 126 15 231-102 302-146 89-303-10-446 10-162 22-296 63-384-55-82-110-33-258 5-370 22-65-42-116 6-156z"
        fill="#ffffff"
        opacity={0.48 + backgroundBreath * 0.14}
      />
      <path
        d="M75 662c126-78 240-47 363-39 138 9 222-54 348-38 96 12 166 58 222 129v134H75z"
        fill="url(#elderly-care-floor)"
      />
      <path
        d="M127 164h236M716 132h222M708 170h152"
        stroke="#ffffff"
        strokeWidth="12"
        strokeLinecap="round"
        opacity="0.52"
      />
      <path
        d="M204 394C340 299 608 287 782 388"
        fill="none"
        stroke={teal[300]}
        strokeWidth="9"
        strokeLinecap="round"
        strokeDasharray="28 28"
        strokeDashoffset={lineDrift}
        opacity="0.24"
      />

      <g opacity={enter} transform={`translate(0 ${artworkLift})`} filter="url(#elderly-care-shadow)">
        <ellipse cx="533" cy="809" rx="424" ry="32" fill="#172e4e" opacity="0.1" />
        <g opacity="0.96">
          {renderElderlyCareLayer(ELDERLY_CARE_STATIC_INDEXES)}
        </g>

        <g transform={`translate(0 ${wheelLift * 0.28})`}>
          <g transform={`rotate(${wheelRock} 890.1 623.8)`}>
            {renderElderlyCareLayer(ELDERLY_CARE_REAR_WHEEL)}
          </g>
          <g transform={`rotate(${wheelRock * 1.2} 696 784.7)`}>
            {renderElderlyCareLayer(ELDERLY_CARE_FRONT_WHEEL)}
          </g>
        </g>

        <g transform={`translate(0 ${elderBob}) rotate(${elderNod} 316 208)`}>
          {renderElderlyCareLayer(ELDERLY_CARE_ELDER_BODY)}
          {renderElderlyCareLayer(ELDERLY_CARE_ELDER_HEAD)}
        </g>

        <g transform={`translate(${reach} ${elderBob * 0.35}) rotate(${wave(frame, fps, 4.8, 0.9)} 408 384)`}>
          {renderElderlyCareLayer(ELDERLY_CARE_REACHING_ARM)}
        </g>

        <g transform={`translate(0 ${caregiverBob}) rotate(${caregiverNod} 760 186)`}>
          {renderElderlyCareLayer(ELDERLY_CARE_CAREGIVER_BODY)}
          {renderElderlyCareLayer(ELDERLY_CARE_CAREGIVER_HEAD)}
        </g>

        <g transform={`translate(${-reach * 0.55} ${caregiverBob * 0.25}) rotate(${wave(frame, fps, 4.4, 0.85, 1.8)} 712 414)`}>
          {renderElderlyCareLayer(ELDERLY_CARE_CAREGIVER_HAND)}
        </g>
      </g>

      <g opacity={enter}>
        <path
          d="M525 407C486 380 467 357 474 330c7-25 38-31 58 0 20-31 51-25 58 0 7 27-13 50-65 77z"
          fill={warm.coral}
          opacity={0.16 + handCare * 0.12}
          transform={`scale(${1 + handCare * 0.04}) translate(${-525 * handCare * 0.04} ${-360 * handCare * 0.04})`}
        />
        <path
          d="M467 398C500 369 555 367 590 398"
          fill="none"
          stroke={warm.gold}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray="150"
          strokeDashoffset={interpolate(connectionDraw, [0, 1], [150, 0])}
          opacity="0.62"
        />
        {[0, 1, 2].map((dot) => {
          const dotPulse = pulse01(frame, fps, 2.7, dot * 0.78);
          return (
            <circle
              key={dot}
              cx={486 + dot * 45}
              cy={385 - Math.sin(dot * 1.2) * 10 + wave(frame, fps, 3.8, 3, dot)}
              r={4 + dotPulse * 3}
              fill={dot === 1 ? warm.gold : teal[500]}
              opacity={0.28 + dotPulse * 0.42}
            />
          );
        })}
      </g>
    </svg>
  );
};

const AboutNursingHomeScene = ({ frame, fps }) => {
  const enter = fadeIn(frame, fps, 0, 1);
  const artworkFloat = wave(frame, fps, 6.8, 7);
  const roomGlow = pulse01(frame, fps, 4.8);
  const heartPulse = pulse01(frame, fps, 2.2);
  const signal = loopProgress(frame, fps, 5.8);
  const signalX = interpolate(signal, [0, 0.34, 0.68, 1], [166, 418, 772, 166], {
    easing: easeInOut,
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const signalY = interpolate(signal, [0, 0.34, 0.68, 1], [188, 118, 184, 188], {
    easing: easeInOut,
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const checkDraw = fadeIn(frame, fps, 0.8, 0.9);
  const shineX = interpolate(loopProgress(frame, fps, 6.5), [0, 1], [-260, 1180], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const artworkTransform = `translateY(${slide(frame, fps, 0, 20) + artworkFloat}px) scale(${0.975 + enter * 0.025})`;
  const sparkles = [
    { x: 165, y: 280, size: 5, phase: 0 },
    { x: 252, y: 150, size: 4, phase: 1.1 },
    { x: 708, y: 126, size: 5, phase: 2.4 },
    { x: 842, y: 298, size: 4, phase: 0.7 },
    { x: 548, y: 92, size: 3.5, phase: 1.8 }
  ];

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #fff7ed 0%, #f0f9f7 47%, #fff1f2 100%)'
      }}
    >
      <svg
        viewBox="0 0 1000 730"
        width="1000"
        height="730"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="about-room-glow" cx="50%" cy="46%" r="58%">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.96" />
            <stop offset="0.58" stopColor="#fff8ed" stopOpacity="0.78" />
            <stop offset="1" stopColor="#dcf2ec" stopOpacity="0.62" />
          </radialGradient>
          <linearGradient id="about-sweep" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="0.48" stopColor="#ffffff" stopOpacity="0.54" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <rect width="1000" height="730" fill="url(#about-room-glow)" />
        <circle cx="500" cy="360" r={255 + roomGlow * 34} fill={warm.gold} opacity={0.09 + roomGlow * 0.08} />
        <circle cx="172" cy="148" r={74 + roomGlow * 10} fill={teal[100]} opacity="0.5" />
        <circle cx="840" cy="146" r={96 + pulse01(frame, fps, 6.2) * 12} fill={warm.rose} opacity="0.34" />
        <path
          d="M128 187C306 91 604 91 838 188"
          fill="none"
          stroke={teal[300]}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray="18 28"
          strokeDashoffset={-signal * 220}
          opacity={0.34}
        />
        <circle cx={signalX} cy={signalY} r={10 + pulse01(frame, fps, 1.7) * 5} fill={teal[700]} opacity="0.9" />
        <circle cx={signalX} cy={signalY} r={26 + pulse01(frame, fps, 1.7) * 18} fill={teal[300]} opacity="0.16" />
      </svg>

      <div
        style={{
          position: 'absolute',
          left: '3%',
          top: '2.5%',
          width: '94%',
          height: '94%',
          opacity: 0.9 + enter * 0.1,
          transform: artworkTransform,
          transformOrigin: '50% 52%',
          filter: 'drop-shadow(0 34px 42px rgba(23, 46, 78, 0.18)) saturate(1.05)'
        }}
      >
        <Img
          src={staticFile('nursing-home.svg')}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
        <AnimatedNursingHomePlants frame={frame} fps={fps} opacity={enter} />
      </div>

      <svg
        viewBox="0 0 1000 730"
        width="1000"
        height="730"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        aria-hidden="true"
      >
        <defs>
          <filter id="about-soft-shadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="16" stdDeviation="14" floodColor="#172e4e" floodOpacity="0.17" />
          </filter>
        </defs>
        <rect
          x={shineX}
          y="-80"
          width="128"
          height="900"
          fill="url(#about-sweep)"
          opacity="0.32"
          transform={`rotate(18 ${shineX + 64} 365)`}
        />

        <g
          opacity={enter}
          transform={`translate(94 ${92 + wave(frame, fps, 5.4, 5)}) scale(${1 + heartPulse * 0.04})`}
          filter="url(#about-soft-shadow)"
        >
          <circle cx="0" cy="0" r="55" fill="#ffffff" opacity="0.95" />
          <circle cx="0" cy="0" r={65 + heartPulse * 22} fill={warm.coral} opacity={0.13} />
          <path
            d="M0 27c-35-24-49-41-43-62 5-19 29-23 43 0 14-23 38-19 43 0 6 21-8 38-43 62z"
            fill={warm.coral}
          />
          <path
            d="M-20 0h14l9-20 16 42 10-24h18"
            stroke="#ffffff"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            opacity={0.9}
          />
        </g>

        <g opacity={fadeIn(frame, fps, 0.25, 0.9)} transform={`translate(740 ${500 + wave(frame, fps, 6.2, 6, 1.5)})`} filter="url(#about-soft-shadow)">
          <rect x="0" y="0" width="174" height="128" rx="28" fill="#ffffff" opacity="0.94" />
          {[0, 1, 2].map((item, index) => (
            <g key={item} transform={`translate(34 ${35 + index * 30})`}>
              <circle cx="0" cy="0" r="11" fill={index === 1 ? warm.gold : teal[100]} />
              <path
                d="M-5 0l4 5 10-12"
                stroke={index === 1 ? '#ffffff' : teal[700]}
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                strokeDasharray="24"
                strokeDashoffset={interpolate(checkDraw, [0, 1], [24, 0])}
              />
              <path d="M24-4h78M24 8h55" stroke={index === 1 ? warm.gold : teal[200]} strokeWidth="7" strokeLinecap="round" opacity="0.86" />
            </g>
          ))}
        </g>

        {sparkles.map((sparkle) => {
          const sparklePulse = pulse01(frame, fps, 2.8, sparkle.phase);
          const size = sparkle.size + sparklePulse * 4;

          return (
            <g
              key={`${sparkle.x}-${sparkle.y}`}
              opacity={0.18 + sparklePulse * 0.7}
              transform={`translate(${sparkle.x} ${sparkle.y}) rotate(${sparklePulse * 45})`}
            >
              <path d={`M0 ${-size}V${size}M${-size} 0H${size}`} stroke={warm.gold} strokeWidth="4" strokeLinecap="round" />
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const AnimatedNursingHomePlants = ({ frame, fps, opacity }) => {
  const leftTallSway = wave(frame, fps, 3.6, 3.4, 0.3);
  const leftSmallSway = wave(frame, fps, 3.2, 4, 1.2);
  const rightSway = wave(frame, fps, 4, 3.2, 2.1);
  const leftTallBreath = 1 + pulse01(frame, fps, 4.1, 0.4) * 0.018;
  const leftSmallBreath = 1 + pulse01(frame, fps, 3.7, 1.3) * 0.02;
  const rightBreath = 1 + pulse01(frame, fps, 4.4, 2) * 0.016;

  return (
    <svg
      viewBox="0 0 1000 730"
      width="1000"
      height="730"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="about-plant-leaf" x1="0" y1="152" x2="0" y2="214" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#71ea7d" />
          <stop offset="1" stopColor="#4bc954" />
        </linearGradient>
        <linearGradient id="about-plant-leaf-shadow" x1="0" y1="152" x2="0" y2="214" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#62db72" />
          <stop offset="1" stopColor="#39ae49" />
        </linearGradient>
        <linearGradient id="about-plant-pot" x1="0" y1="180" x2="0" y2="226" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#b78377" />
          <stop offset="1" stopColor="#875656" />
        </linearGradient>
      </defs>

      <g opacity={opacity}>
        <g transform={`translate(198.7 225.2) rotate(${leftTallSway}) scale(${leftTallBreath}) translate(-198.7 -225.2)`}>
          <path d="M198.9536,192.7792s-8.81-.2963-15.645-15.5565S196.0645,174.0918,198.9536,192.7792Z" fill="url(#about-plant-leaf)" />
          <path d="M196.9386,192.7792s8.81-.2963,15.6451-15.5565S199.8278,174.0918,196.9386,192.7792Z" fill="url(#about-plant-leaf-shadow)" />
          <path d="M198.6215,192.79s-6.8455-5.5542-3.0834-21.8463S207.598,176.1468,198.6215,192.79Z" fill="url(#about-plant-leaf)" />
          <path d="M197.6878,193.7375s8.7082,1.37,18.297-12.3285S204.0477,175.93,197.6878,193.7375Z" fill="url(#about-plant-leaf-shadow)" />
          <path d="M197.8613,193.7375s-8.7082,1.37-18.297-12.3285S191.5014,175.93,197.8613,193.7375Z" fill="url(#about-plant-leaf)" />
        </g>
        <path
          d="M192.6175,193.7375h12.2488a6.2041,6.2041,0,0,1,6.2041,6.2041v25.302a0,0,0,0,1,0,0H186.4135a0,0,0,0,1,0,0v-25.302A6.2041,6.2041,0,0,1,192.6175,193.7375Z"
          fill="url(#about-plant-pot)"
        />

        <g transform={`translate(153.5 225.2) rotate(${leftSmallSway}) scale(${leftSmallBreath}) translate(-153.5 -225.2)`}>
          <path d="M153.7493,210.5869s-8.81-.2962-15.6451-15.5564S150.86,191.9,153.7493,210.5869Z" fill="url(#about-plant-leaf)" />
          <path d="M151.7343,210.5869s8.81-.2962,15.6451-15.5564S154.6234,191.9,151.7343,210.5869Z" fill="url(#about-plant-leaf-shadow)" />
          <path d="M153.4171,210.5975s-6.8454-5.5542-3.0834-21.8464S162.3936,193.9546,153.4171,210.5975Z" fill="url(#about-plant-leaf)" />
          <path d="M152.4835,211.5453s8.7082,1.37,18.297-12.3285S158.8434,193.7375,152.4835,211.5453Z" fill="url(#about-plant-leaf-shadow)" />
          <path d="M152.657,211.5453s-8.7082,1.37-18.297-12.3285S146.297,193.7375,152.657,211.5453Z" fill="url(#about-plant-leaf)" />
        </g>
        <path
          d="M142.5409,206.066h21.9934a4.0714,4.0714,0,0,1,4.0714,4.0714v15.1062a0,0,0,0,1,0,0H138.4695a0,0,0,0,1,0,0V210.1374A4.0714,4.0714,0,0,1,142.5409,206.066Z"
          fill="url(#about-plant-pot)"
        />

        <g transform={`translate(770.1 212.4) rotate(${rightSway}) scale(${rightBreath}) translate(-770.1 -212.4)`}>
          <path d="M770.3429,179.9776s-8.81-.2962-15.645-15.5564S767.4538,161.29,770.3429,179.9776Z" fill="url(#about-plant-leaf)" />
          <path d="M768.3279,179.9776s8.81-.2962,15.6451-15.5564S771.2171,161.29,768.3279,179.9776Z" fill="url(#about-plant-leaf-shadow)" />
          <path d="M770.0108,179.9882s-6.8455-5.5542-3.0834-21.8464S778.9873,163.3452,770.0108,179.9882Z" fill="url(#about-plant-leaf)" />
          <path d="M769.0771,180.9359s8.7082,1.37,18.297-12.3284S775.437,163.1282,769.0771,180.9359Z" fill="url(#about-plant-leaf-shadow)" />
          <path d="M769.2506,180.9359s-8.7082,1.37-18.297-12.3284S762.8907,163.1282,769.2506,180.9359Z" fill="url(#about-plant-leaf)" />
        </g>
        <path
          d="M764.0068,180.9359h12.2488A6.2041,6.2041,0,0,1,782.46,187.14v25.302a0,0,0,0,1,0,0H757.8028a0,0,0,0,1,0,0V187.14A6.2041,6.2041,0,0,1,764.0068,180.9359Z"
          fill="url(#about-plant-pot)"
        />
      </g>
    </svg>
  );
};

const HeroCareScene = ({ frame, fps }) => {
  const enter = fadeIn(frame, fps, 0, 1.1);
  const peopleEnter = fadeIn(frame, fps, 0.2, 1);
  const light = pulse01(frame, fps, 5.6);
  const sunY = wave(frame, fps, 8, 8);
  const plantSway = wave(frame, fps, 4.8, 4);
  const caregiverBob = wave(frame, fps, 5.2, 3);
  const elderBob = wave(frame, fps, 5.4, 2.2, 1.2);
  const handWave = wave(frame, fps, 3.7, 7);
  const heartScale = 1 + pulse01(frame, fps, 2.2) * 0.08;

  return (
    <svg viewBox="0 0 900 680" width="900" height="680">
      <rect width="900" height="680" fill={teal[50]} />
      <path
        d="M86 576C189 505 296 555 395 513C521 459 582 354 727 392C803 412 837 476 847 576H86z"
        fill={teal[100]}
        opacity={0.9}
        transform={`translate(0 ${slide(frame, fps, 0.05, 20)})`}
      />
      <g opacity={enter} transform={`translate(0 ${slide(frame, fps, 0, 26)})`}>
        <rect x="96" y="88" width="708" height="476" rx="44" fill="#fffdf7" />
        <path d="M96 392h708v128c0 24.3-19.7 44-44 44H140c-24.3 0-44-19.7-44-44V392z" fill="#f2eee7" />
        <rect x="145" y="123" width="230" height="196" rx="28" fill="#dff4ef" />
        <path d="M145 246h230v45c0 15.5-12.5 28-28 28H173c-15.5 0-28-12.5-28-28v-45z" fill={teal[100]} />
        <line x1="260" y1="123" x2="260" y2="319" stroke="#ffffff" strokeWidth="10" />
        <line x1="145" y1="220" x2="375" y2="220" stroke="#ffffff" strokeWidth="10" />
        <circle cx="323" cy={170 + sunY} r="34" fill={warm.gold} opacity={0.92} />
        <path
          d="M177 287C222 258 284 257 338 285"
          fill="none"
          stroke="#ffffff"
          strokeWidth="8"
          strokeLinecap="round"
          opacity={0.48 + light * 0.22}
        />
        <rect x="620" y="125" width="115" height="142" rx="20" fill="#ffffff" stroke={teal[100]} strokeWidth="8" />
        <path
          d="M642 209c17-29 42-29 59 0"
          fill="none"
          stroke={teal[500]}
          strokeWidth="8"
          strokeLinecap="round"
          opacity={0.56 + light * 0.3}
        />
        <path d="M647 164h66M647 188h44M647 235h61" stroke={slate[100]} strokeWidth="8" strokeLinecap="round" />
      </g>

      <g opacity={peopleEnter} transform={`translate(0 ${slide(frame, fps, 0.2, 28)})`}>
        <rect x="244" y="441" width="356" height="36" rx="18" fill={warm.sand} />
        <rect x="282" y="474" width="28" height="72" rx="14" fill="#9c7a58" />
        <rect x="534" y="474" width="28" height="72" rx="14" fill="#9c7a58" />

        <g transform={`translate(316 ${351 + elderBob})`}>
          <rect x="-82" y="-2" width="154" height="175" rx="38" fill={teal[200]} />
          <rect x="-96" y="108" width="184" height="72" rx="26" fill={teal[700]} opacity="0.92" />
          <rect x="-25" y="-10" width="30" height="38" rx="13" fill={warm.skinLight} />
          <circle cx="-11" cy="-39" r="41" fill={warm.skinLight} />
          <path d="M-50-53c8-32 38-45 69-34 25 9 36 29 32 53-28-13-59-16-101-19z" fill="#faf1dd" />
          <path d="M-29-27c10 8 23 8 36 0" stroke={slate[700]} strokeWidth="5" strokeLinecap="round" fill="none" />
          <path d="M-76 58c-30 9-50 28-59 63" stroke={warm.skinLight} strokeWidth="16" strokeLinecap="round" fill="none" />
          <path d="M50 55c27 11 48 31 61 61" stroke={warm.skinLight} strokeWidth="16" strokeLinecap="round" fill="none" />
          <path d="M-38 175h-66M31 175h70" stroke={slate[700]} strokeWidth="17" strokeLinecap="round" />
        </g>

        <g transform={`translate(515 ${325 + caregiverBob})`}>
          <path d="M-47 56c14-52 52-78 98-67 43 10 66 45 67 89H-56c0-8 3-16 9-22z" fill={teal[900]} />
          <path d="M-32 62c-29 16-51 40-63 71" stroke={warm.skin} strokeWidth="16" strokeLinecap="round" fill="none" />
          <g transform={`rotate(${handWave} 96 92)`}>
            <path d="M85 22c25 13 44 37 57 70" stroke={warm.skin} strokeWidth="16" strokeLinecap="round" fill="none" />
            <circle cx="144" cy="96" r="10" fill={warm.skin} />
          </g>
          <circle cx="20" cy="-52" r="39" fill={warm.skin} />
          <path d="M-17-62c10-36 59-44 81-13 3 6 5 13 6 20-35-7-61-2-87-7z" fill="#4a2f28" />
          <path d="M9-42c12 10 26 10 40 0" stroke={slate[900]} strokeWidth="5" strokeLinecap="round" fill="none" />
          <rect x="7" y="-19" width="28" height="40" rx="12" fill={warm.skin} />
          <path d="M-5 78h87" stroke={teal[700]} strokeWidth="12" strokeLinecap="round" />
        </g>

        <g transform={`translate(430 ${395 + wave(frame, fps, 4.2, 1.8)})`}>
          <ellipse cx="0" cy="61" rx="56" ry="14" fill="#d7c4a2" opacity="0.5" />
          <rect x="-54" y="20" width="108" height="54" rx="12" fill="#ffffff" stroke={teal[100]} strokeWidth="6" />
          <path d="M22 32h28c19 0 19 28 0 28H35" fill="none" stroke={teal[300]} strokeWidth="7" strokeLinecap="round" />
          <SteamPath x={-28} frame={frame} fps={fps} phase={0} />
          <SteamPath x={0} frame={frame} fps={fps} phase={0.8} />
          <SteamPath x={27} frame={frame} fps={fps} phase={1.6} />
        </g>

        <g transform={`translate(675 377) scale(${heartScale})`}>
          <rect x="-92" y="-58" width="184" height="116" rx="26" fill="#ffffff" stroke={teal[100]} strokeWidth="7" />
          <path d="M0 22C-37-7-57-22-51-45c5-19 29-24 51 0 22-24 46-19 51 0 6 23-14 38-51 67z" fill={warm.coral} />
          <path d="M-61 35h122" stroke={teal[200]} strokeWidth="7" strokeLinecap="round" />
        </g>

        <g transform={`translate(706 450) rotate(${plantSway} 18 62)`}>
          <rect x="0" y="70" width="70" height="54" rx="14" fill={warm.sand} />
          <path d="M36 75C28 35 7 22-11 11M36 75c4-36 24-54 55-60M36 75c-6-28 8-49 35-67" stroke={teal[700]} strokeWidth="8" strokeLinecap="round" fill="none" />
          <ellipse cx="-14" cy="10" rx="22" ry="12" fill={teal[300]} transform="rotate(-22 -14 10)" />
          <ellipse cx="92" cy="15" rx="23" ry="13" fill={teal[500]} transform="rotate(-14 92 15)" />
          <ellipse cx="71" cy="7" rx="21" ry="12" fill={teal[300]} transform="rotate(-35 71 7)" />
        </g>
      </g>
    </svg>
  );
};

const SteamPath = ({ x, frame, fps, phase }) => {
  const progress = loopProgress(frame, fps, 3.2, phase);
  const y = interpolate(progress, [0, 1], [0, -38], {
    easing: easeInOut,
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const opacity = interpolate(progress, [0, 0.2, 0.75, 1], [0, 0.55, 0.3, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const sway = Math.sin(progress * Math.PI * 2) * 8;

  return (
    <path
      d={`M${x} 10c${9 + sway} -11 ${-12 + sway} -17 0 -30`}
      transform={`translate(0 ${y})`}
      fill="none"
      stroke={teal[500]}
      strokeWidth="5"
      strokeLinecap="round"
      opacity={opacity}
    />
  );
};

const ServiceIconScene = ({ frame, fps, type }) => {
  const enter = fadeIn(frame, fps, 0, 0.7);
  const y = slide(frame, fps, 0, 12);

  return (
    <svg viewBox="0 0 280 280" width="280" height="280">
      <rect width="280" height="280" rx="44" fill={teal[100]} opacity={0.72} />
      <circle cx="226" cy="52" r={24 + pulse01(frame, fps, 3.4) * 5} fill="#ffffff" opacity="0.62" />
      <circle cx="49" cy="220" r={18 + pulse01(frame, fps, 4.2, 1) * 4} fill={teal[200]} opacity="0.74" />
      <g opacity={enter} transform={`translate(0 ${y})`}>
        {type === 'personal' && <PersonalCareIcon frame={frame} fps={fps} />}
        {type === 'companion' && <CompanionIcon frame={frame} fps={fps} />}
        {type === 'family' && <FamilyIcon frame={frame} fps={fps} />}
        {type === 'daily' && <DailyLifeIcon frame={frame} fps={fps} />}
      </g>
    </svg>
  );
};

const PersonalCareIcon = ({ frame, fps }) => {
  const drop = loopProgress(frame, fps, 2.8);
  const dropY = interpolate(drop, [0, 0.8, 1], [72, 139, 139], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const dropOpacity = interpolate(drop, [0, 0.18, 0.75, 1], [0, 1, 1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const towel = wave(frame, fps, 3.8, 5);

  return (
    <>
      <rect x="68" y="134" width="144" height="61" rx="22" fill="#ffffff" />
      <path d="M82 148c20 18 93 18 117 0" stroke={teal[300]} strokeWidth="9" strokeLinecap="round" fill="none" />
      <path d="M103 78c28-16 51-16 76 0v34h-76V78z" fill={teal[900]} />
      <path d="M111 112h60" stroke={teal[700]} strokeWidth="13" strokeLinecap="round" />
      <path d={`M80 82c-27 34-13 73 30 80c25 4 49-7 67-27`} stroke={warm.skinLight} strokeWidth="17" strokeLinecap="round" fill="none" />
      <path d={`M185 93c25 26 26 63-1 86`} stroke={warm.skin} strokeWidth="17" strokeLinecap="round" fill="none" />
      <path d={`M181 66c13 24 13 53 0 86`} stroke={warm.rose} strokeWidth="17" strokeLinecap="round" fill="none" transform={`translate(${towel} 0)`} />
      <path
        d="M140 70c12 16 18 27 18 38 0 12-8 22-18 22s-18-10-18-22c0-11 6-22 18-38z"
        fill={teal[500]}
        opacity={dropOpacity}
        transform={`translate(0 ${dropY - 70}) scale(0.48)`}
      />
      <path d="M95 208h90" stroke={slate[200]} strokeWidth="10" strokeLinecap="round" />
    </>
  );
};

const DementiaCareIcon = ({ frame, fps }) => {
  const enter = fadeIn(frame, fps, 0, 0.8);
  const driftX = wave(frame, fps, 4.4, 10);
  const memoryPulse = pulse01(frame, fps, 2.7);
  const orbit = interpolate(loopProgress(frame, fps, 4.8), [0, 1], [0, 360]);

  return (
    <>
      <rect width="280" height="280" rx="44" fill={teal[100]} opacity="0.68" />
      <g opacity={enter} transform={`translate(0 ${slide(frame, fps, 0.2, 12)})`}>
        <g transform="translate(36 46)">
          <ellipse cx="95" cy="188" rx="52" ry="14" fill={warm.sand} opacity="0.52" />
          <rect x="54" y="96" width="82" height="94" rx="24" fill={teal[200]} />
          <circle cx="82" cy="77" r="31" fill={warm.skinLight} />
          <path d="M55 108c14-31 45-40 63-26" stroke="#f2dfc9" strokeWidth="8" strokeLinecap="round" />
          <path d="M45 128c22-14 58-13 75 4" stroke={teal[700]} strokeWidth="9" strokeLinecap="round" />
        </g>
        <g transform="translate(152 98)">
          <ellipse cx="45" cy="142" rx="42" ry="13" fill={warm.sand} opacity="0.48" />
          <rect x="12" y="62" width="66" height="88" rx="22" fill={teal[700]} />
          <circle cx="45" cy="44" r="28" fill={warm.skin} />
          <path d="M25 54l11 14-8 8" stroke="#f2dfc9" strokeWidth="6" strokeLinecap="round" />
          <path d="M65 54l11 14 8 8" stroke="#f2dfc9" strokeWidth="6" strokeLinecap="round" />
        </g>

        <path
          d="M74 170 128 170"
          stroke={teal[500]}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray="70"
          strokeDashoffset={interpolate(enter, [0, 1], [70, 0])}
          fill="none"
          opacity="0.7"
        />

        <g transform={`translate(${84 + driftX} 188)`} opacity="0.8">
          <rect x="-18" y="-40" width="36" height="38" rx="12" fill={teal[300]} />
          <circle cx="0" cy="-58" r="20" fill={warm.skinLight} />
          <path
            d={`M${72 + orbit / 7} 0 c8-24 20-34 34-34`}
            fill="none"
            stroke={slate[500]}
            strokeWidth="5"
          />
        </g>

        <g transform="translate(210 55)">
          <circle r="28" fill={warm.gold} opacity={0.2 + memoryPulse * 0.4} />
          <circle r="14" fill={warm.gold} />
          <rect x="-8" y="24" width="16" height="35" rx="8" fill={teal[900]} />
          <path
            d="M-8 24h16"
            stroke={teal[100]}
            strokeWidth="6"
            strokeLinecap="round"
          />
        </g>
      </g>
    </>
  );
};

const CompanionIcon = ({ frame, fps }) => {
  const lampGlow = 0.38 + pulse01(frame, fps, 3.5) * 0.28;
  const moonX = 180 + drift(frame, fps, 8, 8);
  const waveHand = wave(frame, fps, 3, 8);

  return (
    <>
      <path d="M58 135L140 69l82 66v78H58v-78z" fill="#ffffff" />
      <path d="M45 139L140 62l95 77" stroke={teal[900]} strokeWidth="14" strokeLinecap="round" fill="none" />
      <rect x="94" y="142" width="92" height="71" rx="16" fill={teal[100]} />
      <rect x="121" y="159" width="38" height="54" rx="18" fill={teal[700]} opacity={lampGlow} />
      <circle cx={moonX} cy="53" r="17" fill={warm.gold} opacity="0.86" />
      <circle cx={moonX + 9} cy="47" r="17" fill={teal[100]} opacity="0.92" />
      <g transform={`translate(98 ${wave(frame, fps, 4.5, 2)})`}>
        <circle cx="0" cy="0" r="20" fill={warm.skinLight} />
        <path d="M-31 70c4-41 22-63 57-63s54 22 58 63H-31z" fill={teal[900]} />
        <path d="M34 30c25-1 42 10 54 32" stroke={warm.skinLight} strokeWidth="11" strokeLinecap="round" />
      </g>
      <g transform={`translate(168 ${wave(frame, fps, 4.1, 2, 1)})`}>
        <circle cx="0" cy="0" r="20" fill={warm.skin} />
        <path d="M-34 70c4-42 24-63 58-63s53 21 58 63H-34z" fill={teal[500]} />
        <path d="M-39 31c-20 10-35 27-43 50" stroke={warm.skin} strokeWidth="11" strokeLinecap="round" transform={`rotate(${waveHand} -56 56)`} />
      </g>
      <path d="M78 226h124" stroke={slate[200]} strokeWidth="10" strokeLinecap="round" />
    </>
  );
};

const LiveInCareIcon = ({ frame, fps }) => {
  const monitor = pulse01(frame, fps, 2.9, 1);
  const nurseReach = wave(frame, fps, 4, 4);
  const clockNeedle = loopProgress(frame, fps, 5) * 360;

  return (
    <>
      <rect width="280" height="280" rx="44" fill={teal[100]} opacity="0.72" />
      <g opacity={fadeIn(frame, fps, 0.2, 1)} transform="translate(140 142)">
        <rect x="-110" y="-20" width="220" height="110" rx="28" fill={slate[100]} />
        <path d="M-110 56c0 28 24 50 54 50h114c30 0 56-22 56-50" fill={teal[200]} />
        <rect x="-88" y="-98" width="176" height="72" rx="32" fill={teal[700]} />
        <rect x="52" y="-78" width="52" height="40" rx="12" fill={teal[50]} />
        <path d="M-40 12c-14 30 18 48 40 48h36c14 0 34-12 41-48" fill={teal[300]} />

        <rect x="-96" y="-4" width="42" height="76" rx="20" fill={warm.skin} opacity={0.6 + monitor * 0.4} />
        <rect x="54" y="-4" width="42" height="76" rx="20" fill={warm.skin} />

        <g transform={`translate(${40 + nurseReach} 32)`}>
          <circle cx="0" cy="-52" r="20" fill={warm.skin} />
          <path d="M-33 76c5-48 39-66 72-53" stroke={teal[900]} strokeWidth="11" strokeLinecap="round" fill="none" />
          <path d="M23 64h47" stroke={slate[900]} strokeWidth="7" strokeLinecap="round" />
        </g>

        <circle cx="56" cy="34" r="16" fill={teal[500]} />
        <path
          d="M56 34V16"
          stroke="#ffffff"
          strokeWidth="4"
          strokeLinecap="round"
          transform={`rotate(${clockNeedle} 56 34)`}
        />
        <path
          d="M56 34h11"
          stroke="#ffffff"
          strokeWidth="4"
          strokeLinecap="round"
          transform={`rotate(${clockNeedle / 12} 56 34)`}
        />
      </g>
    </>
  );
};

const RespiteCareIcon = ({ frame, fps }) => {
  const handoff = slide(frame, fps, 0.3, 10);
  const heartRate = pulse01(frame, fps, 1.6, 0) * 5;
  const lamp = wave(frame, fps, 2.6, 4);

  return (
    <>
      <rect width="280" height="280" rx="44" fill={warm.cream} opacity="0.85" />
      <g opacity={fadeIn(frame, fps, 0.2, 0.8)} transform="translate(140 145)">
        <rect x="-112" y="-28" width="224" height="118" rx="30" fill={teal[100]} />
        <path d="M-112 40h224" stroke={teal[200]} strokeWidth="8" />

        <g transform={`translate(-20 ${55 + handoff})`}>
          <path d="M-30 0c-16 0-30 14-30 31s14 31 30 31" fill={warm.skinLight} />
          <rect x="-44" y="-1" width="56" height="64" rx="28" fill={slate[200]} />
          <circle cx="-16" cy="-20" r="16" fill={warm.skinLight} />
        </g>

        <g transform={`translate(56 ${50 - handoff})`}>
          <path d="M-22 0c-16 0-30 14-30 31s14 31 30 31" fill={teal[700]} />
          <rect x="-48" y="-1" width="68" height="64" rx="30" fill={teal[900]} />
          <circle cx="-16" cy="-21" r="16" fill={warm.skin} />
        </g>

        <rect x="-20" y="48" width="70" height="50" rx="14" fill={teal[400]} opacity="0.25" />
        <rect x="-2" y="55" width="22" height="34" rx="10" fill={slate[700]} />
        <rect x="24" y="55" width="22" height="34" rx="10" fill={teal[500]} />

        <path d="M-40 98c33 10 70 10 100 0" stroke={teal[700]} strokeWidth="8" strokeLinecap="round" />
        <circle cx="48" cy="104" r="14" fill={warm.coral} />

        <path
          d="M46 104c0 0-6-11-20-16"
          stroke="#fff"
          strokeWidth="5"
          strokeLinecap="round"
          transform={`translate(0 ${heartRate})`}
        />
        <path
          d="M50 84c8 0 14 8 14 16 0 9-14 19-14 19s-14-10-14-19c0-8 6-16 14-16z"
          fill={warm.coral}
          transform={`translate(${lamp} 0)`}
        />
      </g>
    </>
  );
};

const RehabSupportIcon = ({ frame, fps }) => {
  const assist = wave(frame, fps, 4, 5);
  const stretch = drift(frame, fps, 3.2, 6);
  const steps = interpolate(loopProgress(frame, fps, 2.2), [0, 1], [0, 1]);

  return (
    <>
      <rect width="280" height="280" rx="44" fill={slate[100]} opacity="0.88" />
      <g opacity={fadeIn(frame, fps, 0.2, 1)} transform="translate(140 148)">
        <rect x="-96" y="-2" width="192" height="102" rx="28" fill={teal[700]} />
        <path d="M-96 60h192" stroke={teal[100]} strokeWidth="8" />
        <rect x="-80" y="-88" width="160" height="78" rx="28" fill={teal[100]} />

        <g transform={`translate(${stretch - 6} -38)`}>
          <ellipse cx="0" cy="64" rx="40" ry="16" fill={warm.sand} opacity="0.5" />
          <rect x="-22" y="14" width="44" height="54" rx="18" fill={warm.skin} />
          <circle cx="0" cy="0" r="21" fill={warm.skin} />
          <path
            d="M-18 84c30 0 30 17 0 17s-30-17 0-17"
            fill="none"
            stroke={warm.skinLight}
            strokeWidth="8"
            strokeLinecap="round"
          />
        </g>

        <g transform={`translate(${-26 - assist} 16)`}>
          <rect x="-26" y="-70" width="52" height="70" rx="22" fill={teal[300]} />
          <path d="M-24 -6c7-45 39-66 56-64" stroke={teal[900]} strokeWidth="9" />
          <circle cx="0" cy="-90" r="18" fill={warm.skinLight} />
          <path d="M-20 -74c4-33 28-38 44-22" stroke={slate[700]} strokeWidth="9" fill="none" />
        </g>

        <circle cx="-70" cy="50" r="9" fill={teal[200]} opacity={interpolate(steps, [0, 1], [0.35, 1])} />
        <path
          d="M-70 50h26"
          stroke={teal[700]}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray="34"
          strokeDashoffset={interpolate(steps, [0, 1], [34, 0])}
        />
      </g>
    </>
  );
};

const FamilyIcon = ({ frame, fps }) => {
  const draw = fadeIn(frame, fps, 0.2, 1);
  const heartScale = 1 + pulse01(frame, fps, 2.1) * 0.11;
  const lineOffset = interpolate(draw, [0, 1], [160, 0]);

  return (
    <>
      <path
        d="M89 126C112 101 166 101 191 126"
        stroke={teal[700]}
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray="160"
        strokeDashoffset={lineOffset}
        fill="none"
      />
      <path
        d="M75 178c34 28 96 30 132 0"
        stroke={teal[300]}
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray="170"
        strokeDashoffset={lineOffset}
        fill="none"
      />
      <PersonDot cx="82" cy="143" color={warm.skinLight} body={teal[900]} frame={frame} fps={fps} phase={0} />
      <PersonDot cx="140" cy="111" color={warm.skin} body={teal[500]} frame={frame} fps={fps} phase={0.6} />
      <PersonDot cx="198" cy="143" color={warm.skinLight} body={teal[700]} frame={frame} fps={fps} phase={1.2} />
      <g transform={`translate(140 179) scale(${heartScale})`}>
        <path d="M0 28C-31 6-44-9-39-25c5-15 23-18 39 0 16-18 34-15 39 0 5 16-8 31-39 53z" fill={warm.coral} />
      </g>
      <path d="M84 225h112" stroke={slate[200]} strokeWidth="10" strokeLinecap="round" />
    </>
  );
};

const PersonDot = ({ cx, cy, color, body, frame, fps, phase }) => {
  const x = Number(cx);
  const y = Number(cy);
  const bob = wave(frame, fps, 3.7, 2, phase);

  return (
    <g transform={`translate(${x} ${y + bob})`}>
      <circle cx="0" cy="0" r="20" fill={color} />
      <path d="M-34 68c3-40 23-60 58-60 30 0 48 20 52 60H-34z" fill={body} />
      <path d="M-9 1c7 7 16 7 26 0" stroke={slate[900]} strokeWidth="4" strokeLinecap="round" fill="none" opacity="0.65" />
    </g>
  );
};

const DailyLifeIcon = ({ frame, fps }) => {
  const checkDraw = fadeIn(frame, fps, 0.3, 1);
  const clockAngle = frame * 1.4;
  const spoon = wave(frame, fps, 2.8, 8);

  return (
    <>
      <rect x="74" y="74" width="86" height="124" rx="18" fill="#ffffff" />
      <path d="M95 109h41M95 139h41M95 169h33" stroke={slate[200]} strokeWidth="9" strokeLinecap="round" />
      <path
        d="M90 171l17 16 37-43"
        stroke={teal[700]}
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="88"
        strokeDashoffset={interpolate(checkDraw, [0, 1], [88, 0])}
        fill="none"
      />
      <g transform={`translate(178 143) rotate(${spoon} 24 28)`}>
        <rect x="3" y="11" width="78" height="74" rx="16" fill={warm.gold} opacity="0.26" />
        <path d="M22 77h62" stroke={teal[900]} strokeWidth="11" strokeLinecap="round" />
        <rect x="18" y="35" width="50" height="45" rx="13" fill="#ffffff" />
        <path d="M68 47h18c15 0 15 22 0 22H71" stroke={teal[500]} strokeWidth="7" strokeLinecap="round" fill="none" />
        <SteamPath x={31} frame={frame} fps={fps} phase={0.1} />
        <SteamPath x={48} frame={frame} fps={fps} phase={0.9} />
      </g>
      <g>
        <circle cx="197" cy="76" r="29" fill={teal[100]} stroke={teal[500]} strokeWidth="8" />
        <path d="M197 76V57" stroke={teal[900]} strokeWidth="6" strokeLinecap="round" transform={`rotate(${clockAngle} 197 76)`} />
        <path d="M197 76h16" stroke={teal[900]} strokeWidth="6" strokeLinecap="round" transform={`rotate(${clockAngle / 12} 197 76)`} />
      </g>
      <path d="M77 223h128" stroke={slate[200]} strokeWidth="10" strokeLinecap="round" />
    </>
  );
};

const WhyFamiliesScene = ({ frame, fps }) => {
  const enter = fadeIn(frame, fps, 0, 1);
  const route = fadeIn(frame, fps, 0.3, 1.2);
  const handReach = wave(frame, fps, 3.2, 5);
  const plant = wave(frame, fps, 4.5, 3);
  const notePulse = pulse01(frame, fps, 2.6);

  return (
    <svg viewBox="0 0 900 640" width="900" height="640">
      <rect width="900" height="640" rx="46" fill="#fffdf7" />
      <path d="M0 420h900v220H0z" fill={teal[50]} />
      <path d="M96 120h286v188H96z" fill={teal[100]} />
      <line x1="239" y1="120" x2="239" y2="308" stroke="#ffffff" strokeWidth="9" />
      <line x1="96" y1="215" x2="382" y2="215" stroke="#ffffff" strokeWidth="9" />
      <circle cx="164" cy="169" r={25 + notePulse * 3} fill={warm.gold} opacity="0.88" />

      <g opacity={enter} transform={`translate(0 ${slide(frame, fps, 0, 28)})`}>
        <g transform="translate(382 383)">
          <ellipse cx="0" cy="38" rx="48" ry="13" fill="#d7c4a2" opacity="0.34" />
          <rect x="-43" y="-18" width="86" height="58" rx="18" fill="#ffffff" stroke={teal[100]} strokeWidth="6" opacity="0.9" />
          <SteamPath x={-21} frame={frame} fps={fps} phase={0.2} />
          <SteamPath x={3} frame={frame} fps={fps} phase={1.1} />
          <path d="M31-2h25c17 0 17 27 0 27H38" stroke={teal[300]} strokeWidth="7" strokeLinecap="round" fill="none" opacity="0.9" />
        </g>
        <g transform={`translate(293 ${322 + wave(frame, fps, 4.9, 2)})`}>
          <path d="M-82 84c4-68 34-102 88-102s85 34 91 102H-82z" fill={teal[900]} />
          <path d="M-52 34c22-18 56-20 88-7 17 7 31 19 42 35" stroke={teal[700]} strokeWidth="9" strokeLinecap="round" fill="none" opacity="0.28" />
          <rect x="-13" y="-31" width="31" height="38" rx="13" fill={warm.skinLight} />
          <circle cx="2" cy="-59" r="38" fill="#faf1dd" />
          <path d="M-27-75c15-15 43-16 59 0" stroke="#f2dfc9" strokeWidth="7" strokeLinecap="round" fill="none" opacity="0.55" />
        </g>
        <g transform={`translate(451 ${324 + wave(frame, fps, 4.5, 2.5, 1)})`}>
          <path d="M-75 88c5-72 37-108 95-108s91 36 98 108H-75z" fill={teal[500]} />
          <path d="M-40 35c24-21 59-24 93-8 18 8 32 21 44 38" stroke={teal[700]} strokeWidth="9" strokeLinecap="round" fill="none" opacity="0.24" />
          <rect x="-2" y="-34" width="32" height="41" rx="13" fill={warm.skin} />
          <circle cx="14" cy="-63" r="39" fill="#4a2f28" />
          <path d="M-19-72c22-21 58-21 78 2" stroke="#6a4036" strokeWidth="7" strokeLinecap="round" fill="none" opacity="0.55" />
        </g>

        <rect x="118" y="383" width="462" height="50" rx="25" fill={warm.sand} />
        <rect x="154" y="429" width="32" height="88" rx="16" fill="#9c7a58" />
        <rect x="510" y="429" width="32" height="88" rx="16" fill="#9c7a58" />
        <g>
          <path d={`M230 404c33-35 74-39 116-13`} stroke={warm.skinLight} strokeWidth="15" strokeLinecap="round" fill="none" />
          <circle cx={348 + handReach * 0.35} cy="392" r="9" fill={warm.skinLight} />
          <path d={`M498 386c-35 4-65 10-91 24`} stroke={warm.skin} strokeWidth="15" strokeLinecap="round" fill="none" />
          <circle cx={403 - handReach * 0.35} cy="411" r="9" fill={warm.skin} />
        </g>
      </g>

      <g opacity={enter} transform={`translate(${slide(frame, fps, 0.1, 34)} 0)`}>
        <rect x="620" y="128" width="168" height="128" rx="24" fill={slate[50]} stroke={teal[100]} strokeWidth="7" />
        <path
          d="M653 219C674 170 718 210 747 158"
          fill="none"
          stroke={teal[500]}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray="170"
          strokeDashoffset={interpolate(route, [0, 1], [170, 0])}
        />
        <circle cx="653" cy="219" r={13 + notePulse * 4} fill={warm.coral} />
        <circle cx="747" cy="158" r={13 + pulse01(frame, fps, 2.9, 1) * 4} fill={teal[900]} />
        <path d="M667 122l31-33 31 33" stroke={teal[900]} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <rect x="674" y="123" width="49" height="45" rx="9" fill={teal[100]} />
        <rect x="692" y="141" width="14" height="27" rx="7" fill={teal[700]} />
      </g>

      <g opacity={enter} transform={`translate(664 ${356 + wave(frame, fps, 4.7, 2)})`}>
        <rect x="-76" y="-53" width="196" height="124" rx="26" fill="#ffffff" stroke={teal[100]} strokeWidth="7" />
        <circle cx="-34" cy="-5" r={22 + notePulse * 3} fill={teal[100]} />
        <path d="M-45-5l8 8 17-21" stroke={teal[700]} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <path d="M5-22h80M5 2h61M5 27h88" stroke={slate[200]} strokeWidth="8" strokeLinecap="round" />
      </g>

      <g transform={`translate(728 444) rotate(${plant} 24 70)`}>
        <rect x="0" y="77" width="78" height="58" rx="16" fill={warm.sand} />
        <path d="M39 82C24 38 2 19-27 9M39 82c5-46 31-70 79-72M39 82c-6-35 11-63 50-82" stroke={teal[700]} strokeWidth="8" strokeLinecap="round" fill="none" />
        <ellipse cx="-32" cy="8" rx="25" ry="13" fill={teal[300]} transform="rotate(-15 -32 8)" />
        <ellipse cx="119" cy="10" rx="25" ry="14" fill={teal[500]} transform="rotate(-19 119 10)" />
        <ellipse cx="89" cy="0" rx="23" ry="13" fill={teal[300]} transform="rotate(-35 89 0)" />
      </g>
    </svg>
  );
};

const ServicesHeroScene = ({ frame, fps }) => {
  const enter = fadeIn(frame, fps, 0, 0.9);
  const route = fadeIn(frame, fps, 0.15, 1.2);
  const pathPulse = pulse01(frame, fps, 2.4);
  const carerBob = wave(frame, fps, 5, 3);
  const clockAngle = loopProgress(frame, fps, 8) * 360;

  return (
    <svg viewBox="0 0 900 520" width="900" height="520">
      <rect width="900" height="520" rx="48" fill={teal[50]} />
      <path d="M0 352c140-42 260-28 392 12 160 49 288 47 508-36v192H0z" fill={teal[100]} opacity="0.7" />
      <g opacity={enter} transform={`translate(0 ${slide(frame, fps, 0, 22)})`}>
        <rect x="120" y="124" width="302" height="220" rx="38" fill="#fffdf7" stroke={teal[100]} strokeWidth="8" />
        <path d="M150 157L271 67L392 157" stroke={teal[900]} strokeWidth="15" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <path d="M180 161L271 94L362 161" stroke={teal[100]} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.95" />
        <rect x="205" y="236" width="84" height="108" rx="34" fill={teal[700]} />
        <rect x="315" y="192" width="56" height="52" rx="14" fill={teal[100]} />
        <circle cx="158" cy="288" r={12 + pathPulse * 5} fill={warm.coral} opacity="0.9" />

        <path
          d="M413 252C489 158 625 164 710 252"
          stroke={teal[300]}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray="390"
          strokeDashoffset={interpolate(route, [0, 1], [390, 0])}
          fill="none"
        />

        <g transform={`translate(518 ${254 + carerBob})`}>
          <ellipse cx="0" cy="118" rx="72" ry="18" fill={warm.sand} opacity="0.45" />
          <rect x="-48" y="2" width="96" height="118" rx="34" fill={teal[500]} />
          <rect x="-13" y="-19" width="28" height="40" rx="12" fill={warm.skin} />
          <circle cx="0" cy="-48" r="35" fill={warm.skin} />
          <path d="M-34 -61C-27 -78 -9 -86 12 -82C29 -78 39 -67 41 -55C16 -61 -9 -64 -34 -61Z" fill="#4a2f28" />
          <path d="M-17-38c11 8 24 8 36 0" stroke={slate[900]} strokeWidth="5" strokeLinecap="round" fill="none" />
        </g>

        <g transform="translate(683 136)">
          <rect x="0" y="0" width="128" height="186" rx="30" fill="#ffffff" stroke={teal[100]} strokeWidth="8" />
          <circle cx="64" cy="58" r="33" fill={teal[100]} />
          <path d="M64 58V35" stroke={teal[900]} strokeWidth="7" strokeLinecap="round" transform={`rotate(${clockAngle} 64 58)`} />
          <path d="M64 58h22" stroke={teal[900]} strokeWidth="7" strokeLinecap="round" transform={`rotate(${clockAngle / 12} 64 58)`} />
          <path d="M34 124h59M34 151h42" stroke={slate[200]} strokeWidth="9" strokeLinecap="round" />
          <path d="M30 118l12 12 23-29" stroke={teal[700]} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </g>

        <ServicePill x={172} y={382} label="Personal" delay={0.2} frame={frame} fps={fps} />
        <ServicePill x={333} y={398} label="Companion" delay={0.35} frame={frame} fps={fps} />
        <ServicePill x={532} y={386} label="Live-in" delay={0.5} frame={frame} fps={fps} />
        <ServicePill x={686} y={388} label="Rehab" delay={0.65} frame={frame} fps={fps} />
      </g>
    </svg>
  );
};

const ServicePill = ({ x, y, label, delay, frame, fps }) => {
  const opacity = fadeIn(frame, fps, delay, 0.6);
  const widths = {
    Personal: 124,
    Companion: 150,
    'Live-in': 116,
    Rehab: 104
  };
  const width = widths[label] || Math.max(112, label.length * 12 + 42);
  const left = -width / 2;
  const dotX = left + 24;
  const textX = dotX + 18;

  return (
    <g opacity={opacity} transform={`translate(${x} ${y + slide(frame, fps, delay, 10)})`}>
      <rect x={left} y="-22" width={width} height="44" rx="22" fill="#ffffff" stroke={teal[100]} strokeWidth="5" />
      <circle cx={dotX} cy="0" r="9" fill={teal[500]} />
      <text x={textX} y="6" fontSize="17" fontFamily="Inter, Arial, sans-serif" fontWeight="700" fill={slate[700]}>
        {label}
      </text>
    </g>
  );
};

const ServicesTopicScene = ({ frame, fps, type }) => {
  if (['day', 'night', 'palliative', 'family-support'].includes(type)) {
    return <ServicesMiniScene frame={frame} fps={fps} type={type} />;
  }

  return (
    <svg viewBox="0 0 640 520" width="640" height="520">
      <rect width="640" height="520" rx="42" fill="#fffdf7" />
      <path d="M0 352c113-34 223-29 333 2 120 34 207 39 307 2v164H0z" fill={teal[50]} />
      <circle cx="544" cy="82" r={24 + pulse01(frame, fps, 4) * 5} fill={warm.gold} opacity="0.18" />
      <circle cx="92" cy="444" r={18 + pulse01(frame, fps, 3.4, 1) * 4} fill={teal[100]} />
      {type === 'personal' && <PersonalServiceScene frame={frame} fps={fps} />}
      {type === 'companion' && <CompanionServiceScene frame={frame} fps={fps} />}
      {type === 'dementia' && <DementiaServiceScene frame={frame} fps={fps} />}
      {type === 'livein' && <LiveInServiceScene frame={frame} fps={fps} />}
      {type === 'respite' && <RespiteServiceScene frame={frame} fps={fps} />}
      {type === 'rehab' && <RehabServiceScene frame={frame} fps={fps} />}
      {type === 'quality' && <QualityServiceScene frame={frame} fps={fps} />}
    </svg>
  );
};

const PersonalServiceScene = ({ frame, fps }) => {
  const towelSway = wave(frame, fps, 3.8, 5);
  const droplet = loopProgress(frame, fps, 2.4);
  const dropY = interpolate(droplet, [0, 0.8, 1], [172, 249, 249], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const dropOpacity = interpolate(droplet, [0, 0.15, 0.78, 1], [0, 1, 1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });

  return (
    <g opacity={fadeIn(frame, fps, 0.1, 0.9)} transform={`translate(0 ${slide(frame, fps, 0.1, 18)})`}>
      <rect x="80" y="84" width="160" height="178" rx="30" fill={teal[100]} />
      <rect x="110" y="119" width="100" height="82" rx="22" fill="#ffffff" stroke={teal[200]} strokeWidth="7" />
      <path d="M134 232h92" stroke={teal[700]} strokeWidth="11" strokeLinecap="round" />
      <path d="M165 145c9 12 19 18 32 19" stroke={teal[500]} strokeWidth="7" strokeLinecap="round" fill="none" />
      <path d="M180 174c11 14 16 24 16 35 0 14-9 24-21 24s-21-10-21-24c0-11 8-22 26-35z" fill={teal[500]} opacity={dropOpacity} transform={`translate(0 ${dropY - 174}) scale(0.52)`} />

      <g transform={`translate(378 290) rotate(${towelSway} 14 42)`}>
        <path d="M0 0c52 3 91 35 109 91" stroke={warm.rose} strokeWidth="20" strokeLinecap="round" fill="none" />
        <path d="M4 0c49 9 78 37 93 78" stroke="#ffffff" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.7" />
      </g>

      <g transform={`translate(330 ${276 + wave(frame, fps, 4.8, 2)})`}>
        <ellipse cx="0" cy="112" rx="86" ry="18" fill={warm.sand} opacity="0.45" />
        <rect x="-54" y="2" width="108" height="116" rx="34" fill={teal[700]} />
        <rect x="-16" y="-20" width="34" height="40" rx="14" fill={warm.skinLight} />
        <circle cx="0" cy="-50" r="36" fill={warm.skinLight} />
        <path d="M-35 -63C-29 -80 -11 -88 10 -84C28 -80 39 -69 41 -57C17 -63 -10 -66 -35 -63Z" fill="#f2dfc9" />
        <path d="M50 45c41-9 75 1 101 30" stroke={warm.skinLight} strokeWidth="16" strokeLinecap="round" fill="none" />
      </g>

      <rect x="116" y="314" width="388" height="44" rx="22" fill={warm.sand} />
      <path d="M148 358h308" stroke="#b99267" strokeWidth="12" strokeLinecap="round" opacity="0.45" />
    </g>
  );
};

const CompanionServiceScene = ({ frame, fps }) => {
  const chatA = 0.35 + pulse01(frame, fps, 3) * 0.35;
  const chatB = 0.35 + pulse01(frame, fps, 3.2, 1.2) * 0.35;

  return (
    <g opacity={fadeIn(frame, fps, 0.1, 0.9)} transform={`translate(0 ${slide(frame, fps, 0.1, 18)})`}>
      <rect x="112" y="308" width="416" height="48" rx="24" fill={warm.sand} />
      <rect x="165" y="355" width="28" height="82" rx="14" fill="#9c7a58" />
      <rect x="447" y="355" width="28" height="82" rx="14" fill="#9c7a58" />

      <g transform={`translate(230 ${259 + wave(frame, fps, 4.2, 2)})`}>
        <rect x="-57" y="20" width="114" height="108" rx="34" fill={teal[900]} />
        <rect x="-17" y="-17" width="34" height="38" rx="14" fill={warm.skinLight} />
        <circle cx="0" cy="-46" r="34" fill={warm.skinLight} />
        <path d="M-34 -59C-28 -75 -11 -82 9 -78C26 -75 37 -64 39 -53C16 -59 -9 -62 -34 -59Z" fill="#faf1dd" />
        <path d="M45 56c39 3 70 20 91 52" stroke={warm.skinLight} strokeWidth="15" strokeLinecap="round" fill="none" />
      </g>

      <g transform={`translate(407 ${258 + wave(frame, fps, 4.4, 2.2, 1)})`}>
        <rect x="-60" y="23" width="120" height="108" rx="35" fill={teal[500]} />
        <rect x="-16" y="-16" width="34" height="40" rx="14" fill={warm.skin} />
        <circle cx="0" cy="-47" r="34" fill={warm.skin} />
        <path d="M-35 -60C-29 -76 -11 -83 11 -79C29 -75 40 -64 42 -52C17 -59 -10 -62 -35 -60Z" fill="#4a2f28" />
        <path d="M-48 59c-38 4-68 21-88 50" stroke={warm.skin} strokeWidth="15" strokeLinecap="round" fill="none" />
      </g>

      <g transform="translate(318 316)">
        <rect x="-47" y="-18" width="94" height="54" rx="15" fill="#ffffff" stroke={teal[100]} strokeWidth="6" />
        <path d="M30-3h25c17 0 17 25 0 25H38" stroke={teal[300]} strokeWidth="7" strokeLinecap="round" fill="none" />
        <SteamPath x={-18} frame={frame} fps={fps} phase={0.1} />
        <SteamPath x={8} frame={frame} fps={fps} phase={1.1} />
      </g>

      <g transform="translate(116 112)" opacity={chatA}>
        <rect x="0" y="0" width="138" height="78" rx="22" fill="#ffffff" stroke={teal[100]} strokeWidth="6" />
        <path d="M32 29h74M32 52h52" stroke={teal[300]} strokeWidth="8" strokeLinecap="round" />
      </g>
      <g transform="translate(392 98)" opacity={chatB}>
        <rect x="0" y="0" width="138" height="78" rx="22" fill="#ffffff" stroke={teal[100]} strokeWidth="6" />
        <path d="M32 29h74M32 52h52" stroke={slate[200]} strokeWidth="8" strokeLinecap="round" />
      </g>
    </g>
  );
};

const DementiaServiceScene = ({ frame, fps }) => {
  const active = Math.floor(loopProgress(frame, fps, 4.8) * 3);
  const clockAngle = loopProgress(frame, fps, 9) * 360;
  const route = fadeIn(frame, fps, 0.3, 1);

  return (
    <g opacity={fadeIn(frame, fps, 0.1, 0.9)} transform={`translate(0 ${slide(frame, fps, 0.1, 18)})`}>
      <rect x="74" y="86" width="210" height="248" rx="32" fill="#ffffff" stroke={teal[100]} strokeWidth="8" />
      {['Morning', 'Lunch', 'Medication'].map((label, index) => (
        <g key={label} transform={`translate(104 ${126 + index * 64})`}>
          <rect width="150" height="43" rx="16" fill={active === index ? teal[100] : slate[50]} />
          <circle cx="23" cy="22" r={active === index ? 12 : 8} fill={active === index ? warm.gold : teal[300]} />
          <text x="46" y="28" fontSize="17" fontFamily="Inter, Arial, sans-serif" fontWeight="700" fill={slate[700]}>
            {label}
          </text>
        </g>
      ))}

      <g transform="translate(427 142)">
        <circle cx="0" cy="0" r="54" fill={teal[100]} stroke={teal[300]} strokeWidth="9" />
        <path d="M0 0V-34" stroke={teal[900]} strokeWidth="8" strokeLinecap="round" transform={`rotate(${clockAngle} 0 0)`} />
        <path d="M0 0h31" stroke={teal[900]} strokeWidth="8" strokeLinecap="round" transform={`rotate(${clockAngle / 12} 0 0)`} />
      </g>

      <path
        d="M327 286C366 228 435 239 470 286"
        stroke={teal[500]}
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray="210"
        strokeDashoffset={interpolate(route, [0, 1], [210, 0])}
        fill="none"
      />
      <circle cx="327" cy="286" r={12 + pulse01(frame, fps, 2.1) * 4} fill={warm.coral} />
      <circle cx="470" cy="286" r={12 + pulse01(frame, fps, 2.1, 1) * 4} fill={teal[900]} />

      <rect x="348" y="326" width="178" height="74" rx="24" fill="#ffffff" stroke={teal[100]} strokeWidth="7" />
      <path d="M379 363l22 22 47-58" stroke={teal[700]} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </g>
  );
};

const LiveInServiceScene = ({ frame, fps }) => {
  const lampGlow = 0.35 + pulse01(frame, fps, 3.4) * 0.32;
  const clockAngle = loopProgress(frame, fps, 7.5) * 360;

  return (
    <g opacity={fadeIn(frame, fps, 0.1, 0.9)} transform={`translate(0 ${slide(frame, fps, 0.1, 18)})`}>
      <path d="M110 221L320 68l210 153v204H110V221z" fill="#ffffff" stroke={teal[100]} strokeWidth="8" />
      <path d="M90 226L320 58l230 168" stroke={teal[900]} strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <rect x="162" y="267" width="224" height="86" rx="28" fill={teal[100]} />
      <rect x="190" y="242" width="86" height="50" rx="23" fill="#fffdf7" />
      <path d="M188 318h194" stroke={teal[300]} strokeWidth="10" strokeLinecap="round" />

      <g transform="translate(430 180)">
        <circle cx="0" cy="0" r={36 + lampGlow * 11} fill={warm.gold} opacity="0.14" />
        <path d="M-24 0h48l-13 42h-22z" fill={warm.gold} opacity={0.72} />
        <rect x="-7" y="41" width="14" height="56" rx="7" fill={teal[900]} />
      </g>

      <g transform="translate(246 310)">
        <circle cx="0" cy="-54" r="24" fill={warm.skin} />
        <rect x="-33" y="-33" width="66" height="78" rx="26" fill={teal[700]} />
        <path d="M34-8c34 2 55 18 64 47" stroke={warm.skin} strokeWidth="12" strokeLinecap="round" fill="none" />
      </g>

      <g transform="translate(474 287)">
        <circle cx="0" cy="0" r="33" fill={teal[100]} stroke={teal[400]} strokeWidth="7" />
        <path d="M0 0V-21" stroke={teal[900]} strokeWidth="6" strokeLinecap="round" transform={`rotate(${clockAngle} 0 0)`} />
        <path d="M0 0h19" stroke={teal[900]} strokeWidth="6" strokeLinecap="round" transform={`rotate(${clockAngle / 12} 0 0)`} />
      </g>
    </g>
  );
};

const RespiteServiceScene = ({ frame, fps }) => {
  const transfer = interpolate(loopProgress(frame, fps, 4.8), [0, 0.5, 1], [-36, 36, -36], {
    easing: easeInOut,
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const heart = 1 + pulse01(frame, fps, 1.8) * 0.08;

  return (
    <g opacity={fadeIn(frame, fps, 0.1, 0.9)} transform={`translate(0 ${slide(frame, fps, 0.1, 18)})`}>
      <rect x="82" y="145" width="190" height="220" rx="34" fill={teal[100]} />
      <rect x="370" y="145" width="190" height="220" rx="34" fill={slate[100]} />
      <g transform="translate(178 257)">
        <circle cx="0" cy="-70" r="31" fill={warm.skinLight} />
        <rect x="-48" y="-42" width="96" height="118" rx="34" fill={slate[200]} />
        <path d="M45 7c40 7 71 27 93 61" stroke={warm.skinLight} strokeWidth="15" strokeLinecap="round" fill="none" />
      </g>
      <g transform="translate(466 257)">
        <circle cx="0" cy="-70" r="31" fill={warm.skin} />
        <path d="M-37-75c18-30 59-27 75-2-25-4-47-3-75 2z" fill="#4a2f28" />
        <rect x="-48" y="-42" width="96" height="118" rx="34" fill={teal[900]} />
        <path d="M-45 8c-40 6-71 26-92 61" stroke={warm.skin} strokeWidth="15" strokeLinecap="round" fill="none" />
      </g>
      <g transform={`translate(${320 + transfer} 255)`}>
        <rect x="-45" y="-60" width="90" height="112" rx="20" fill="#ffffff" stroke={teal[200]} strokeWidth="7" />
        <path d="M-18-17h42M-18 9h34M-18 35h48" stroke={slate[200]} strokeWidth="8" strokeLinecap="round" />
        <g transform={`translate(0 -33) scale(${heart})`}>
          <path d="M0 18c-25-18-35-30-31-43 4-12 19-15 31 0 12-15 27-12 31 0 4 13-6 25-31 43z" fill={warm.coral} />
        </g>
      </g>
      <path d="M264 418c41 15 79 15 118 0" stroke={teal[700]} strokeWidth="10" strokeLinecap="round" opacity="0.5" />
    </g>
  );
};

const RehabServiceScene = ({ frame, fps }) => {
  const walk = wave(frame, fps, 2.4, 7);
  const progress = loopProgress(frame, fps, 4.5);

  return (
    <g opacity={fadeIn(frame, fps, 0.1, 0.9)} transform={`translate(0 ${slide(frame, fps, 0.1, 18)})`}>
      <rect x="84" y="108" width="472" height="252" rx="40" fill={slate[50]} stroke={teal[100]} strokeWidth="8" />
      <path d="M135 354h360" stroke={teal[200]} strokeWidth="12" strokeLinecap="round" />
      <g transform={`translate(${266 + walk} 257)`}>
        <circle cx="0" cy="-84" r="31" fill={warm.skinLight} />
        <rect x="-33" y="-57" width="66" height="92" rx="25" fill={teal[500]} />
        <path d="M-29 32c-17 29-21 58-12 87" stroke={warm.skinLight} strokeWidth="13" strokeLinecap="round" fill="none" />
        <path d="M30 31c20 24 30 52 30 85" stroke={warm.skinLight} strokeWidth="13" strokeLinecap="round" fill="none" />
        <path d="M-78-23h154M-78-23v143M76-23v143" stroke={teal[900]} strokeWidth="10" strokeLinecap="round" fill="none" />
      </g>
      {[0, 1, 2, 3].map((step, index) => (
        <circle
          key={step}
          cx={146 + index * 92}
          cy="404"
          r={index / 4 < progress ? 14 : 9}
          fill={index / 4 < progress ? teal[500] : slate[200]}
        />
      ))}
      <rect x="394" y="132" width="112" height="46" rx="23" fill="#ffffff" stroke={teal[100]} strokeWidth="6" />
      <path d="M418 155h61" stroke={teal[700]} strokeWidth="8" strokeLinecap="round" />
    </g>
  );
};

const QualityServiceScene = ({ frame, fps }) => {
  const draw = fadeIn(frame, fps, 0.25, 1);
  const pulse = pulse01(frame, fps, 1.8);

  return (
    <g opacity={fadeIn(frame, fps, 0.1, 0.9)} transform={`translate(0 ${slide(frame, fps, 0.1, 18)})`}>
      <rect x="96" y="98" width="250" height="320" rx="34" fill="#ffffff" stroke={teal[100]} strokeWidth="8" />
      <rect x="161" y="70" width="120" height="58" rx="22" fill={teal[700]} />
      {[0, 1, 2].map((item, index) => (
        <g key={item} transform={`translate(139 ${172 + index * 72})`}>
          <circle cx="0" cy="0" r="20" fill={teal[100]} />
          <path
            d="M-9 0l8 8 19-24"
            stroke={teal[700]}
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            strokeDasharray="50"
            strokeDashoffset={interpolate(draw, [0, 1], [50, 0])}
          />
          <path d="M42-8h114M42 16h82" stroke={slate[200]} strokeWidth="9" strokeLinecap="round" />
        </g>
      ))}
      <g transform="translate(454 216)">
        <path d="M0-92c52 14 86 14 138 0v92c0 65-42 106-69 121C42 106 0 65 0 0v-92z" fill={teal[900]} />
        <path d="M38-6h24l15-31 23 70 16-39h29" stroke="#ffffff" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity={0.75 + pulse * 0.25} />
        <circle cx="69" cy="-47" r={12 + pulse * 5} fill={warm.gold} />
      </g>
    </g>
  );
};

const ServicesMiniScene = ({ frame, fps, type }) => {
  const enter = fadeIn(frame, fps, 0, 0.7);
  const loop = loopProgress(frame, fps, 4);
  const clock = loop * 360;
  const clockPositions = {
    day: { x: 222, y: 70, scale: 0.72 },
    night: { x: 224, y: 68, scale: 0.66 }
  };
  const clockPosition = clockPositions[type];

  return (
    <svg viewBox="0 0 280 280" width="280" height="280">
      <rect width="280" height="280" rx="44" fill={teal[100]} opacity="0.72" />
      <circle cx="225" cy="58" r={22 + pulse01(frame, fps, 3.6) * 4} fill="#ffffff" opacity="0.62" />
      <g opacity={enter} transform={`translate(0 ${slide(frame, fps, 0, 12)})`}>
        {type === 'day' && (
          <>
            <circle cx="88" cy="82" r={28 + pulse01(frame, fps, 3) * 4} fill={warm.gold} />
            <rect x="76" y="134" width="128" height="84" rx="24" fill="#ffffff" />
            <path d="M104 168h72M104 194h46" stroke={teal[300]} strokeWidth="9" strokeLinecap="round" />
            <path d="M176 132c24 12 40 34 46 66" stroke={teal[700]} strokeWidth="9" strokeLinecap="round" fill="none" />
          </>
        )}
        {type === 'night' && (
          <>
            <circle cx={92 + drift(frame, fps, 6, 8)} cy="78" r="28" fill={warm.gold} />
            <circle cx={104 + drift(frame, fps, 6, 8)} cy="68" r="28" fill={teal[100]} />
            <rect x="62" y="151" width="158" height="62" rx="24" fill="#ffffff" />
            <path d="M82 151v-36c0-24 18-42 42-42h34c24 0 42 18 42 42v36" stroke={teal[700]} strokeWidth="11" strokeLinecap="round" fill="none" />
            <path d="M101 184h78" stroke={teal[300]} strokeWidth="9" strokeLinecap="round" />
          </>
        )}
        {type === 'palliative' && (
          <>
            <g transform={`translate(140 125) scale(${1 + pulse01(frame, fps, 2) * 0.08})`}>
              <path d="M0 43c-47-32-66-54-58-82 7-25 38-31 58 0 20-31 51-25 58 0 8 28-11 50-58 82z" fill={warm.coral} />
            </g>
            <path d="M84 198c38 24 75 24 112 0" stroke={teal[700]} strokeWidth="10" strokeLinecap="round" fill="none" />
            <path d="M94 178h92" stroke={teal[300]} strokeWidth="9" strokeLinecap="round" />
          </>
        )}
        {type === 'family-support' && (
          <>
            <path d="M70 143c31-45 109-45 140 0" stroke={teal[700]} strokeWidth="11" strokeLinecap="round" fill="none" />
            <PersonDot cx="88" cy="145" color={warm.skinLight} body={teal[900]} frame={frame} fps={fps} phase={0} />
            <PersonDot cx="140" cy="117" color={warm.skin} body={teal[500]} frame={frame} fps={fps} phase={0.9} />
            <PersonDot cx="192" cy="145" color={warm.skinLight} body={teal[700]} frame={frame} fps={fps} phase={1.6} />
          </>
        )}
        {clockPosition && (
          <g transform={`translate(${clockPosition.x} ${clockPosition.y}) scale(${clockPosition.scale})`}>
            <circle r="29" fill="#ffffff" stroke={teal[300]} strokeWidth="7" />
            <path d="M0 0V-18" stroke={teal[900]} strokeWidth="6" strokeLinecap="round" transform={`rotate(${clock} 0 0)`} />
            <path d="M0 0h16" stroke={teal[900]} strokeWidth="6" strokeLinecap="round" transform={`rotate(${clock / 12} 0 0)`} />
          </g>
        )}
      </g>
    </svg>
  );
};

const RatingScene = ({ frame, fps }) => {
  const shineX = interpolate(loopProgress(frame, fps, 3.6), [0, 1], [-34, 302], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });

  return (
    <svg viewBox="0 0 280 58" width="280" height="58">
      <rect x="1" y="1" width="278" height="56" rx="28" fill="#ffffff" opacity="0.02" />
      {[0, 1, 2, 3, 4].map((star, index) => {
        const scale = 1 + pulse01(frame, fps, 2.4, index * 0.62) * 0.1;
        const opacity = 0.88 + pulse01(frame, fps, 2.7, index) * 0.12;

        return (
          <g key={star} transform={`translate(${18 + index * 50} 6) scale(${scale})`}>
            <path d={starPath} fill={warm.gold} opacity={opacity} />
            <path d={starPath} fill="none" stroke="#d99425" strokeWidth="2" opacity="0.45" />
          </g>
        );
      })}
      <rect x={shineX} y="0" width="25" height="58" fill="#ffffff" opacity="0.28" transform={`rotate(18 ${shineX} 29)`} />
    </svg>
  );
};

const CareConversationScene = ({ frame, fps }) => {
  const enter = fadeIn(frame, fps, 0, 0.9);
  const route = fadeIn(frame, fps, 0.25, 1.1);
  const callWave = pulse01(frame, fps, 2.2);
  const dot = loopProgress(frame, fps, 4);
  const dotX = interpolate(dot, [0, 0.5, 1], [186, 455, 714], {
    easing: easeInOut,
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });
  const dotY = interpolate(dot, [0, 0.5, 1], [150, 86, 150], {
    easing: easeInOut,
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp'
  });

  return (
    <svg viewBox="0 0 900 260" width="900" height="260">
      <rect width="900" height="260" fill="transparent" />
      <g opacity={enter} transform={`translate(0 ${slide(frame, fps, 0, 16)})`}>
        <path
          d="M185 150C302 65 602 65 715 150"
          fill="none"
          stroke={teal[200]}
          strokeWidth="13"
          strokeLinecap="round"
          strokeDasharray="680"
          strokeDashoffset={interpolate(route, [0, 1], [680, 0])}
        />
        <circle cx={dotX} cy={dotY} r="11" fill={warm.gold} />
        <circle cx={dotX} cy={dotY} r={17 + pulse01(frame, fps, 1.5) * 7} fill={warm.gold} opacity="0.18" />

        <g transform="translate(84 68)">
          <rect x="0" y="38" width="158" height="116" rx="26" fill="#ffffff" opacity="0.95" />
          <path d="M18 57l61-50 61 50" stroke="#ffffff" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <path d="M18 57l61-50 61 50" stroke={teal[100]} strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <rect x="58" y="96" width="42" height="58" rx="18" fill={teal[700]} opacity={0.75 + callWave * 0.18} />
          <circle cx="36" cy="86" r={10 + pulse01(frame, fps, 2.8) * 3} fill={warm.coral} />
        </g>

        <g transform="translate(391 42)">
          <rect x="0" y="0" width="118" height="176" rx="28" fill="#ffffff" opacity="0.96" />
          <rect x="17" y="24" width="84" height="110" rx="18" fill={teal[50]} />
          <path d="M40 79c10 19 24 33 44 42l18-18-19-16-12 11c-10-6-18-14-24-24l11-12-16-19-18 18c3 6 5 12 6 18z" fill={teal[900]} />
          <path d="M104 53c18 8 30 23 35 43" stroke="#ffffff" strokeWidth="8" strokeLinecap="round" fill="none" opacity={0.44 + callWave * 0.34} />
          <path d="M113 29c33 14 56 42 64 80" stroke="#ffffff" strokeWidth="8" strokeLinecap="round" fill="none" opacity={0.22 + callWave * 0.28} />
          <circle cx="59" cy="152" r="9" fill={teal[200]} />
        </g>

        <g transform="translate(655 64)">
          <rect x="0" y="32" width="162" height="116" rx="26" fill="#ffffff" opacity="0.95" />
          <rect x="34" y="0" width="94" height="112" rx="18" fill={teal[100]} />
          <path
            d="M53 56l19 19 43-52"
            stroke={teal[900]}
            strokeWidth="10"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            strokeDasharray="118"
            strokeDashoffset={interpolate(fadeIn(frame, fps, 0.75, 0.9), [0, 1], [118, 0])}
          />
          <path d="M34 30h94" stroke="#ffffff" strokeWidth="8" strokeLinecap="round" />
          <circle cx="130" cy="105" r={10 + pulse01(frame, fps, 2.7, 1) * 4} fill={warm.coral} />
        </g>
      </g>
    </svg>
  );
};
